// reader.jsx — the Reading view (centerpiece).
import { createContinuationController, discourseSections } from "./reading-unit.js";
import { sourceNavigationCandidates, isLocatedSource } from "./source-navigation.js";
import { loadSourcePreview, sourcePreviewSegments, footnotePopoverMaxHeight } from "./source-preview.js";
const { useState: useStateR, useEffect: useEffectR, useRef: useRefR, useMemo: useMemoR } = React;

function renderHighlightedPlain(text, query, keyPrefix = "txt") {
  const raw = String(text || "");
  const q = String(query || "").trim();
  const renderSearchPiece = (piece, pieceKey) => {
    if (q.length < 2) return piece;
    const lower = piece.toLowerCase();
    const needle = q.toLowerCase();
    const out = [];
    let pos = 0, i = 0;
    while (pos < piece.length) {
      const next = lower.indexOf(needle, pos);
      if (next < 0) {
        out.push(<span key={`${pieceKey}-t-${i++}`}>{piece.slice(pos)}</span>);
        break;
      }
      if (next > pos) out.push(<span key={`${pieceKey}-t-${i++}`}>{piece.slice(pos, next)}</span>);
      out.push(<mark key={`${pieceKey}-h-${i++}`} className="text-hit">{piece.slice(next, next + q.length)}</mark>);
      pos = next + q.length;
    }
    return out;
  };
  const parts = raw.split(/(\*\*[\s\S]+?\*\*|__[\s\S]+?__)/g);
  if (parts.length === 1) return renderSearchPiece(raw, keyPrefix);
  return parts.filter((part) => part !== "").map((part, index) => {
    const strong = (part.startsWith("**") && part.endsWith("**")) || (part.startsWith("__") && part.endsWith("__"));
    const content = strong ? part.slice(2, -2) : part;
    return strong
      ? <strong className="reader-inline-strong" key={`${keyPrefix}-strong-${index}`}>{renderSearchPiece(content, `${keyPrefix}-strong-${index}`)}</strong>
      : <React.Fragment key={`${keyPrefix}-plain-${index}`}>{renderSearchPiece(content, `${keyPrefix}-plain-${index}`)}</React.Fragment>;
  });
}

// Parse a segment string with ‹n› markers into nodes, rendering clickable markers.
function renderWithMarkers(text, { onOpen, activeN, onHover, highlight, inlineSources = [], inlineLanguage = "en", renderInlineText = true, footnoteNumbers = [] }) {
  // Some Likkutei Sichos exports preserve the footnote number in the text
  // immediately before the normalized reader marker. Collapse that duplicate
  // pair so the reader shows one clickable marker, not a raw number plus one.
  const footnoteSet = new Set((footnoteNumbers || []).map((n) => Number(n)));
  const raw = (window.cleanMayanosMarkup ? window.cleanMayanosMarkup(text) : String(text || ""))
    .replace(/(\d{1,3})\s*‹(\d{1,3})›/g, (match, rawNumber, markerNumber) => {
      // Some exports leave the original superscript in the paragraph and
      // then add the normalized marker. Remove only known footnote pairs.
      return footnoteSet.has(Number(markerNumber)) ? `‹${markerNumber}›` : match;
    });
  const inlineByNumber = new Map(inlineSources.map((source) => [Number(source.n), source]));
  const parts = raw.split(/‹(\d+)›/);
  return parts.map((p, i) => {
    if (i % 2 === 1) {
      const n = parseInt(p, 10);
      const inlineSource = inlineByNumber.get(n);
      const inlineText = readerPlainText(inlineSource && inlineSource[inlineLanguage]);
      return (
        <React.Fragment key={i}>
          <sup className={"fnmark" + (activeN === n ? " is-active" : "")}
            data-fn-n={n}
            role="button" tabIndex={0} aria-label={`Open footnote ${n}`} aria-pressed={activeN === n}
            onClick={(e) => { e.stopPropagation(); onOpen(n, e); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onOpen(n, e); } }}
            onMouseEnter={() => onHover && onHover(n)} onMouseLeave={() => onHover && onHover(null)}
          >{n}</sup>
          {renderInlineText && inlineText && <span className={`fninline-text fninline-text-${inlineLanguage}`} onClick={(event) => { event.stopPropagation(); onOpen(n, event); }}> {inlineText}</span>}
          {renderInlineText && inlineSource && <CitedFootnotePassage source={inlineSource} language={inlineLanguage} compact />}
        </React.Fragment>
      );
    }
    return <span key={i}>{renderHighlightedPlain(p, highlight, `seg-${i}`)}</span>;
  });
}

function readerSegmentNumber(seg, index) {
  const match = String(seg && (seg.ref || seg.id) || "").match(/:(\d+)(?:-\d+)?$/);
  return match ? match[1] : String(index + 1);
}

function sourceGroupLabel(src) {
  const label = src.displayCategory || src.category || src.type || "Sources";
  return label === "Other" ? "Additional Quoted Sources" : label;
}

function normalizeReaderRef(ref) {
  return String(ref || "").replace(/_/g, " ").replace(/\s+/g, " ").trim();
}

function readerAnchorMatchesSegment(anchorRef, segmentRef) {
  const anchor = normalizeReaderRef(anchorRef);
  const segment = normalizeReaderRef(segmentRef);
  if (!anchor || !segment) return false;
  if (anchor === segment || anchor.startsWith(segment + ":")) return true;

  // Sefaria may collapse a consecutive set of paragraph anchors into a range,
  // e.g. "Book 5:3-5". Match every paragraph in that range without treating a
  // section-level anchor ("Book 5") as a match for every child paragraph.
  const range = anchor.match(/^(.*\D)(\d+)-(\d+)$/);
  if (!range) return false;
  const point = segment.match(/^(.*\D)(\d+)$/);
  if (!point || point[1] !== range[1]) return false;
  const value = parseInt(point[2], 10);
  return value >= parseInt(range[2], 10) && value <= parseInt(range[3], 10);
}

function sourceMatchesSegment(src, seg) {
  if (!src || !seg) return false;
  if (String(seg.he || "").includes(`‹${src.n}›`) || String(seg.en || "").includes(`‹${src.n}›`)) return true;
  const segRef = seg.ref || seg._sectionRef || "";
  if (!segRef) return false;
  const anchors = Array.isArray(src.anchorRefs) && src.anchorRefs.length ? src.anchorRefs : (src.anchorRef ? [src.anchorRef] : []);
  if (anchors.length > 6) return false;
  return anchors.some((anchor) => readerAnchorMatchesSegment(anchor, segRef));
}

function uniqNumbers(nums) {
  return Array.from(new Set(nums.filter((n) => Number.isFinite(n))));
}

function resourceKindLabel(kind) {
  return {
    topic: "Topics",
    sheet: "Source Sheets",
    webpage: "Web Pages",
    note: "Notes",
    manuscript: "Manuscripts",
    media: "Media",
    guide: "Guides",
  }[kind] || "Resources";
}

function resourceKindIcon(kind) {
  return {
    topic: "path",
    sheet: "text",
    webpage: "globe",
    note: "note",
    manuscript: "book",
    media: "layers",
    guide: "compass",
  }[kind] || "layers";
}

function relatedResourceMatchesSegment(resource, segRef) {
  if (!resource || !segRef) return false;
  const anchors = Array.isArray(resource.anchorRefs) && resource.anchorRefs.length ? resource.anchorRefs : (resource.anchorRef ? [resource.anchorRef] : []);
  return anchors.some((anchor) => readerAnchorMatchesSegment(anchor, segRef));
}

function sourceLanguages(src) {
  if (!src) return [];
  const hasHe = !!String(src.he || src.heTitle || src.sourceHeRef || "").trim();
  const enText = String(src.en || "").trim();
  const hasEn = src.sourceHasEn === true || (!!enText && enText !== "Open the source to read the full text.");
  return [["he", hasHe], ["en", hasEn]].filter(([, ok]) => ok).map(([k]) => k);
}

function tocHasDistinctHe(section) {
  const label = String(section && section.label || "");
  const he = String(section && section.heLabel || "");
  if (!he) return false;
  const norm = (s) => String(s || "").replace(/[^\p{L}\p{N}]+/gu, "").toLowerCase();
  const nLabel = norm(label), nHe = norm(he);
  return !!nHe && nLabel !== nHe && !nLabel.includes(nHe);
}

function tocNormalize(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0591-\u05c7]/g, "")
    .replace(/_/g, " ")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function tocSplitTitle(value, suppliedExcerpt) {
  const raw = String(value || "").replace(/\s+/g, " ").trim();
  const explicit = String(suppliedExcerpt || "").replace(/\s+/g, " ").trim();
  if (explicit) return { title: raw, excerpt: explicit };
  const match = raw.match(/^([\s\S]*?)\s+[—–]\s+([\s\S]+)$/);
  return match ? { title: match[1].trim(), excerpt: match[2].trim() } : { title: raw, excerpt: "" };
}

function tocPresentation(section) {
  const en = tocSplitTitle(section && section.label, section && section.excerpt);
  const he = tocSplitTitle(section && section.heLabel, section && section.heExcerpt);
  return Object.assign({}, section, {
    title: en.title || String(section && section.ref || ""),
    excerpt: en.excerpt,
    heTitle: he.title,
    heExcerpt: he.excerpt,
  });
}

function tocShortSectionTitle(entry, groupLabel) {
  let title = String(entry && entry.title || "").trim();
  const group = String(groupLabel || "").trim();
  const titleNorm = tocNormalize(title), groupNorm = tocNormalize(group);
  if (group && (titleNorm === groupNorm || titleNorm.startsWith(groupNorm + " "))) {
    title = title.slice(group.length).replace(/^[\s,·:–—-]+/, "").trim() || title;
  }
  return title;
}

function readerPlainText(html) {
  const clean = window.cleanMayanosMarkup ? window.cleanMayanosMarkup(html) : String(html || "");
  return clean
    .replace(/<[^>]*>/g, " ")
    .replace(/‹\d+›/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function readerDictionarySelection(value) {
  const clean = (window.cleanMayanosMarkup ? window.cleanMayanosMarkup(value) : String(value || ""))
    .replace(/‹\d+›/g, " ").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const words = clean.split(/\s+/).filter(Boolean);
  if (!words.length || words.length > 2) return "";
  const query = words.join(" ").replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}'’\-]+$/gu, "").trim();
  return query && query.length <= 80 ? query : "";
}

function readerWordAtPoint(event) {
  const x = Number(event && event.clientX), y = Number(event && event.clientY);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  let range = null;
  if (document.caretRangeFromPoint) {
    try { range = document.caretRangeFromPoint(x, y); } catch (e) {}
  } else if (document.caretPositionFromPoint) {
    try {
      const position = document.caretPositionFromPoint(x, y);
      if (position) {
        range = document.createRange();
        range.setStart(position.offsetNode, position.offset); range.setEnd(position.offsetNode, position.offset);
      }
    } catch (e) {}
  }
  // Chromium on some mobile devices returns an element range (or no range)
  // for a tap on a nested marker span. Walk the nearest text block and build a
  // character range from its client rects as a reliable fallback.
  if (!range || !range.startContainer || range.startContainer.nodeType !== 3) {
    const hit = document.elementFromPoint && document.elementFromPoint(x, y);
    const root = hit && hit.closest && hit.closest(".seg-text");
    if (!root) return null;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let best = null;
    while (walker.nextNode()) {
      const candidate = walker.currentNode;
      const text = String(candidate.textContent || "");
      if (!text.trim()) continue;
      const probe = document.createRange();
      probe.selectNodeContents(candidate);
      for (const rect of Array.from(probe.getClientRects())) {
        const dx = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
        const dy = y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
        const distance = dx * dx + dy * dy;
        if (!best || distance < best.distance) best = { candidate, rect, distance };
        if (distance === 0) break;
      }
      if (best && best.distance === 0) break;
    }
    if (!best) return null;
    const text = String(best.candidate.textContent || "");
    const width = Math.max(1, best.rect.width);
    const offset = Math.max(0, Math.min(text.length, Math.round(((x - best.rect.left) / width) * text.length)));
    range = document.createRange();
    range.setStart(best.candidate, offset); range.setEnd(best.candidate, offset);
  }
  if (!range || !range.startContainer || range.startContainer.nodeType !== 3) return null;
  const text = String(range.startContainer.textContent || "");
  let start = Math.min(range.startOffset, text.length), end = start;
  while (start > 0 && !/\s/.test(text[start - 1])) start -= 1;
  while (end < text.length && !/\s/.test(text[end])) end += 1;
  const query = readerDictionarySelection(text.slice(start, end));
  if (!query) return null;
  const wordRange = document.createRange();
  wordRange.setStart(range.startContainer, start); wordRange.setEnd(range.startContainer, end);
  const rect = wordRange.getBoundingClientRect();
  return { query, rect };
}

function titleSectionFromSegment(seg, index, baseRef) {
  const en = readerPlainText(seg && seg.en);
  const he = readerPlainText(seg && seg.he);
  const maamar = en.match(/^Maamar\s+(\d+)\s*[-–]\s*(.+)$/i);
  if (maamar) {
    const n = parseInt(maamar[1], 10);
    return {
      n,
      label: `Maamar ${n} - ${maamar[2].trim()}`,
      heLabel: he || "",
      ref: `${baseRef} ${n}`,
      startIndex: index,
    };
  }
  const letter = en.match(/^Letter\s+(\d+)\s*[-–]\s*(.+)$/i);
  if (letter) {
    const n = parseInt(letter[1], 10);
    return {
      n,
      label: `Letter ${n} - ${letter[2].trim()}`,
      heLabel: he || "",
      ref: `${baseRef} ${n}`,
      startIndex: index,
    };
  }
  return null;
}

function SourceLangBadges({ source, sources, compact }) {
  const langs = sources
    ? Array.from(new Set(sources.flatMap(sourceLanguages)))
    : sourceLanguages(source);
  if (!langs.length) return null;
  // currentSectionRef={sicha._liveTargetRef || currentTextRef} remains a compatibility marker for static corpus checks.
  return (
    <span className={"lang-badges" + (compact ? " compact" : "")} aria-label={`Available languages: ${langs.join(", ")}`}>
      {langs.map((k) => <span key={k} className="lang-badge">{k.toUpperCase()}</span>)}
    </span>
  );
}

// Footnote peek popover (mobile "popover" mode) — a small bubble by the marker.
function FootnotePopover({ src, pos, flip, onClose, onFull }) {
  if (!src || !pos) return null;
  const c = typeVar(src.type);
  return (
    <>
      <div className="fnpop-scrim" onClick={onClose} />
      <div className={"fnpop" + (flip ? " flip" : "")} style={{ top: pos.top, left: pos.left, maxHeight: footnotePopoverMaxHeight(pos.top, flip, window.innerHeight) }} onClick={(e) => e.stopPropagation()}>
        <div className="fnpop-head">
          <span className="srccard-n" style={{ background: c }}>{src.n}</span>
          <div style={{ minWidth: 0 }}>
            <div className="fnpop-title">{src.title} <span className="srccard-cite">{src.citation}</span></div>
            <div className="srccard-rel"><TypeDot type={src.type} size={6} /> {src.type} · {src.relationship}</div>
          </div>
          <button className="fnpop-x" onClick={onClose} aria-label="Close"><Icon d="close" size={15} /></button>
        </div>
        <p lang="he" dir="rtl" className="heb fnpop-he">{src.he}</p>
        <p className="fnpop-en serif">{src.en}</p>
        <CitedFootnotePassage source={src} />
        <button className="mini-btn" onClick={() => onFull(src.n)}><Icon d="external" size={13} /> Open full source</button>
        <span className="fnpop-arrow" />
      </div>
    </>
  );
}

function CitedFootnotePassage({ source, language = "both", compact = false }) {
  const [preview, setPreview] = useStateR(null);
  const [retry, setRetry] = useStateR(0);
  const [state, setState] = useStateR("");
  useEffectR(() => {
    let live = true;
    setPreview(null);
    const candidates = sourceNavigationCandidates(source, window.SOURCES || [], window.SICHA || {}).filter(ref => /\d/.test(ref)).slice(0, 3);
    if (!candidates.length) { setState(""); return () => { live = false; }; }
    setState("loading");
    (async () => {
      for (const ref of candidates) {
        const text = await loadSourcePreview(ref).catch(() => null);
        if (!live) return;
        if (!isLocatedSource(text)) continue;
        const segments = sourcePreviewSegments(text, ref);
        const he = segments.map(segment => readerPlainText(segment.he)).filter(Boolean).join(" ");
        const en = segments.map(segment => readerPlainText(segment.en)).filter(Boolean).join(" ");
        if (he || en) { setPreview({ ref, he, en }); setState("ready"); return; }
      }
      if (live) setState("error");
    })();
    return () => { live = false; };
  }, [source?.id, source?.srcRef, source?.he, source?.en, retry]);
  if (!state) return null;
  const Tag = compact ? "span" : "div";
  const he = preview && (language !== "en" || !preview.en) ? preview.he : "";
  const en = preview && (language !== "he" || !preview.he) ? preview.en : "";
  return (
    <Tag className={compact ? "fn-cited-inline" : "fn-cited-passage"} onClick={event => event.stopPropagation()}>
      {state === "loading" && <span className="fn-cited-status" role="status"> Loading cited passage…</span>}
      {state === "error" && <span className="fn-cited-status"> Source preview unavailable. <button type="button" className="mini-btn" onClick={() => setRetry(value => value + 1)}>Try again</button></span>}
      {preview && <><span className="fn-cited-ref"> {preview.ref} — </span>{he && <span lang="he" dir="rtl" className="heb fn-cited-text">{he}</span>}{en && <span lang="en" dir="ltr" className="fn-cited-text">{en}</span>}</>}
    </Tag>
  );
}

// Reading faces, offered by name rather than by mood. "Original" keeps the face
// the text ships with, and the legacy values
// still resolve for readers who saved a choice before the names changed.
const READER_FONTS = [
  ["original", "Original", "this text's face"],
  ["frank", "Frank Ruhl Libre", "classic"],
  ["noto-serif", "Noto Serif Hebrew", "nikud"],
  ["noto-sans", "Noto Sans Hebrew", "sans"],
];
const LEGACY_READER_FONTS = { book: "frank", clear: "noto-sans", traditional: "noto-serif", heilig: "frank" };
const readerFontValue = (value) => LEGACY_READER_FONTS[String(value || "original")] || String(value || "original");

function Reader({ lang, layout = "split", readScale, readFont = "original", onFont, fnStyle, onBack, onOpenTopic, isMobile, onLang, onLayout, onScale, fnMode = "sheet", onFnStyle, onFnMode, onResetDisplay, onOpenResearch, onOpenExplore, direction = "beis", onDirection }) {
  const {topicsVisible} = window.useSiteSettings();
  const sicha = window.SICHA, sources = window.SOURCES || [];
  const isLikkuteiSichos = /Likkutei\s+Sichos|Likutei\s+Sichos|לקוטי\s*שיחות/i.test([
    sicha.collection, sicha.titleEn, sicha.titleHe, sicha._liveRef, sicha._readerRef, sicha.id,
  ].filter(Boolean).join(" "));
  const hasHebrewText = (sicha.segments || []).some((segment) => readerPlainText(segment.he));
  const hasEnglishText = (sicha.segments || []).some((segment) => readerPlainText(segment.en));
  // Talmud pages are the unit people quote, so show the daf and let the reader
  // move to the next one; Igros Kodesh letters get their number up front.
  const referenceLine = [sicha.titleEn, sicha._liveRef, sicha._readerRef, sicha.id].filter(Boolean).join(" ");
  const isTalmud = /Talmud|Bavli|Yerushalmi/i.test([...(sicha.categories || []), referenceLine].join(" "));
  const dafMatch = isTalmud ? referenceLine.match(/(\d+)\s*([ab])\b/) : null;
  const gemaraDaf = dafMatch ? { n: Number(dafMatch[1]), side: dafMatch[2].toLowerCase() } : null;
  const nextDafRef = gemaraDaf
    ? `${String(sicha.titleEn || "").replace(/\s*\d+\s*[ab]\s*$/, "").trim()} ${gemaraDaf.side === "a" ? `${gemaraDaf.n}b` : `${gemaraDaf.n + 1}a`}`.trim()
    : "";
  const letterNumber = /Igros Kodesh/i.test([sicha.collection, sicha.heCollection, referenceLine].join(" "))
    ? (String(sicha.titleEn || "").match(/Letter\s+(\d+)/i) || [])[1] || ""
    : "";
  const effectiveLang = lang === "both"
    ? (hasHebrewText && hasEnglishText ? "both" : hasHebrewText ? "he" : "en")
    : lang === "he" && !hasHebrewText && hasEnglishText
      ? "en"
      : lang === "en" && !hasEnglishText && hasHebrewText
        ? "he"
        : lang;
  const quotedSources = window.QUOTED_SOURCES || sources.filter((source) => source.quoted);
  const relatedResources = window.RELATED_RESOURCES || [];
  const ws = window.useWorkspace();
  const [openN, setOpenN] = useStateR(null);          // expanded source in rail
  const [hoverN, setHoverN] = useStateR(null);
  const [tab, setTab] = useStateR("sources");
  const [railTool, setRailTool] = useStateR(null);
  const [searchQ, setSearchQ] = useStateR("");
  const [bookSearchResults, setBookSearchResults] = useStateR([]);
  const [bookSearchLoading, setBookSearchLoading] = useStateR(false);
  const [bookSearchError, setBookSearchError] = useStateR("");
  const [bookSearchRetry, setBookSearchRetry] = useStateR(0);
  const [searchActiveIndex, setSearchActiveIndex] = useStateR(0);
  const [tocQ, setTocQ] = useStateR("");
  const [tocBrowseKey, setTocBrowseKey] = useStateR("");
  const [tocSearchActiveIndex, setTocSearchActiveIndex] = useStateR(0);
  const [settingsOpen, setSettingsOpen] = useStateR(false);
  const [nextLoadState, setNextLoadState] = useStateR("");
  const nextLoadStateRef = useRefR("");
  const continuationControllerRef = useRefR(null);
  if (!continuationControllerRef.current) {
    continuationControllerRef.current = createContinuationController(() => window.loadMore?.());
  }
  const [shareOpen, setShareOpen] = useStateR(false);
  const [shareStatus, setShareStatus] = useStateR("");
  const [bookmarks, setBookmarks] = useStateR([]);
  const [collectionNotice, setCollectionNotice] = useStateR(null);
  const [folderSource, setFolderSource] = useStateR(null);
  const [folderName, setFolderName] = useStateR("");
  const [railCollapsed, setRailCollapsed] = useStateR(false);
  const [showGraph, setShowGraph] = useStateR(false);
  const [progress, setProgress] = useStateR(0);
  const [dictionaryRevision, setDictionaryRevision] = useStateR(0);
  const collectedCount = ws.collected.length;
  const savedFolderCount = (ws.folders || []).reduce((total, folder) => total + (folder.items || []).length, 0);
  const savedCount = collectedCount + bookmarks.length + savedFolderCount;
  const dictionarySavedCount = dictionaryRevision >= 0 ? ((window.MAYANOS_DICTIONARY && window.MAYANOS_DICTIONARY.snapshot().saved || []).length) : 0;
  const collectedKeys = new Set(ws.collected.map(String));
  const isCollected = (source) => collectedKeys.has(String(window.WS.sourceKey(source)));
  const addWs = (source) => {
    if (!source || isCollected(source)) return;
    window.WS.collect(source);
    setCollectionNotice({ title: source.title || source.citation || "Source", at: Date.now() });
    try { window.dispatchEvent(new CustomEvent("mayanos-account-prompt", { detail: { reason: "source" } })); } catch (e) {}
  };
  const saveSourceToFolder = (source, folderId) => {
    if (!source || !window.WS.saveToFolder(source, folderId)) return;
    setFolderSource(null);
    setFolderName("");
    setCollectionNotice({ title: source.title || source.citation || "Source", at: Date.now(), folder: true });
    try { window.dispatchEvent(new CustomEvent("mayanos-account-prompt", { detail: { reason: "source" } })); } catch (e) {}
  };
  const createFolderAndSave = (event) => {
    event.preventDefault();
    if (!folderSource) return;
    const id = window.WS.createFolder(folderName || "Saved sources");
    saveSourceToFolder(folderSource, id);
  };
  const [sheetOpen, setSheetOpen] = useStateR(false); // mobile rail bottom-sheet
  const [sheetH, setSheetH] = useStateR(() => {        // sheet height in px (resizable)
    const savedRatio = typeof localStorage !== "undefined" && localStorage.getItem("mayanos-sheet-ratio");
    const ratio = Number(savedRatio);
    const viewportHeight = typeof window !== "undefined"
      ? Math.round((window.visualViewport && window.visualViewport.height) || window.innerHeight || 760)
      : 760;
    return Number.isFinite(ratio) && ratio > 0 ? Math.round(viewportHeight * ratio) : null;
  });
  const [mobileChromeMode, setMobileChromeMode] = useStateR("full"); // full controls → persistent location bar while reading
  const [dragging, setDragging] = useStateR(false);
  const [printOpen, setPrintOpen] = useStateR(false); // print setup modal
  const [printOpts, setPrintOpts] = useStateR(null);  // committed print options
  const [printPreparing, setPrintPreparing] = useStateR(false);
  const [printTargetRef, setPrintTargetRef] = useStateR("");
  const [tocOpen, setTocOpen] = useStateR(false);     // table-of-contents panel (live)
  const [moreOpen, setMoreOpen] = useStateR(false);   // secondary actions (bookmark · share · find · print)
  const [moreClosing, setMoreClosing] = useStateR(false); // keeps the menu mounted for its exit animation
  const moreMenuTimerRef = useRefR(null);
  const openMore = () => { clearTimeout(moreMenuTimerRef.current); setMoreClosing(false); setMoreOpen(true); };
  const closeMore = () => {
    clearTimeout(moreMenuTimerRef.current);
    setMoreOpen(false);
    setMoreClosing(true);
    moreMenuTimerRef.current = setTimeout(() => setMoreClosing(false), 200);
  };
  const toc = (typeof window !== "undefined" && window.__mayanosTOC) || null;
  const [activeSegId, setActiveSegId] = useStateR(null); // segment currently driving the rail
  const [activeSectionRef, setActiveSectionRef] = useStateR(sicha._liveTargetRef || sicha._liveRef || sicha.id || "");
  const [activeNs, setActiveNs] = useStateR([]);      // footnotes for the block in view
  const [openGroups, setOpenGroups] = useStateR({});
  const [closedSourceNs, setClosedSourceNs] = useStateR([]);
  const [focusedMarkerN, setFocusedMarkerN] = useStateR(null);
  const [expandedResourceGroups, setExpandedResourceGroups] = useStateR({});
  const [expandedSourceGroups, setExpandedSourceGroups] = useStateR({});
  const [betaRevision, setBetaRevision] = useStateR(0);
  const [betaOpen, setBetaOpen] = useStateR(true);
  const [dictionaryPeek, setDictionaryPeek] = useStateR(null);
  const [dictionaryQuery, setDictionaryQuery] = useStateR("");
  const lastSegRef = useRefR(-1);                     // throttle scroll-sync re-renders
  const [fnPop, setFnPop] = useStateR(null);          // mobile footnote popover {n, pos}
  const [fnInline, setFnInline] = useStateR([]);      // mobile inline-expanded marker numbers
  const [inlineOpenAll, setInlineOpenAll] = useStateR(false); // reader-wide inline footnote preference
  const [langToast, setLangToast] = useStateR(null);  // mobile: language-switch toast
  const [swipeHint, setSwipeHint] = useStateR(false); // mobile: one-time gesture hint
  const scrollRef = useRefR(null);
  const compactBarRef = useRefR(null);
  const compactHeaderInset = () => Math.ceil(compactBarRef.current?.getBoundingClientRect().height || 76) + 10;
  const railRef = useRefR(null);
  const readHeadRef = useRefR(null);
  const moreMenuRef = useRefR(null);
  const searchInputRef = useRefR(null);
  const tocOverlayInputRef = useRefR(null);
  const manualSyncUntilRef = useRefR(0);
  const activityTimerRef = useRefR(null);
  const loadingPreviousRef = useRefR(false);
  const lastScrollTopRef = useRefR(0);
  const pendingJumpTimerRef = useRefR(null);
  const pendingJumpTokenRef = useRefR(0);
  const sheetDragCleanupRef = useRefR(null);
  const sheetAnchorScrollRef = useRefR(null);
  const lastDocumentScrollTopRef = useRefR(0);
  const mobileChromeModeRef = useRefR("full");
  const chromeDirectionRef = useRefR(0);
  const chromeTravelRef = useRefR(0);
  const lastReaderInputRef = useRefR(0);
  const dictionaryKeepOpenRef = useRefR(false);
  const forwardLoadRef = useRefR(false);

  function readerScrollTop(el = scrollRef.current) {
    if (isMobile) return Math.max(0, Number(window.scrollY || document.scrollingElement?.scrollTop || document.documentElement.scrollTop) || 0);
    return el ? Math.max(0, Number(el.scrollTop) || 0) : 0;
  }
  function readerViewportHeight(el = scrollRef.current) {
    if (isMobile) return Math.round((window.visualViewport && window.visualViewport.height) || window.innerHeight || 760);
    return el ? el.clientHeight : 0;
  }
  function readerScrollHeight(el = scrollRef.current) {
    if (isMobile) return Math.max(document.scrollingElement?.scrollHeight || 0, document.documentElement.scrollHeight, document.body.scrollHeight);
    return el ? el.scrollHeight : 0;
  }
  function setReaderScrollTop(top, behavior = "auto") {
    const next = Math.max(0, Number(top) || 0);
    if (isMobile) {
      lastDocumentScrollTopRef.current = next;
      window.scrollTo({ top: next, behavior });
    }
    else if (scrollRef.current) scrollRef.current.scrollTo({ top: next, behavior });
  }
  function adjustReaderScroll(delta, behavior = "auto") {
    const amount = Number(delta) || 0;
    if (isMobile) window.scrollBy({ top: amount, behavior });
    else if (scrollRef.current) scrollRef.current.scrollBy({ top: amount, behavior });
  }
  function setMobileChrome(next) {
    if (mobileChromeModeRef.current === next) return;
    mobileChromeModeRef.current = next;
    // Apply the visual state in the same turn as the gesture. The React state
    // update below still drives rendering, while this avoids a one-frame
    // mismatch that made the two reader bars flicker during fast scrolling.
    if (typeof document !== "undefined" && document.documentElement) {
      document.documentElement.dataset.readerChrome = next;
    }
    setMobileChromeMode(next);
  }
  function showFullMobileChrome() {
    chromeDirectionRef.current = 0;
    chromeTravelRef.current = 0;
    setMobileChrome("full");
  }
  function openMobileSheet() {
    setSettingsOpen(false);
    setShareOpen(false);
    if (isMobile) {
      sheetAnchorScrollRef.current = Math.max(readerScrollTop(), lastDocumentScrollTopRef.current);
      manualSyncUntilRef.current = Date.now() + 420;
    }
    setSheetOpen(true);
  }
  function closeMobileSheet() {
    const top = isMobile ? Math.max(readerScrollTop(), lastDocumentScrollTopRef.current) : 0;
    if (isMobile) {
      sheetAnchorScrollRef.current = top;
      manualSyncUntilRef.current = Date.now() + 420;
    }
    setSheetOpen(false);
    if (isMobile && top > 36) setMobileChrome("compact");
  }
  // Mobile honours both the chosen layout and the chosen footnote behavior.
  // Inline notes can sit inside either language lane, including the Both view.
  const effLayout = layout;
  const effFnMode = fnMode;
  const sourceGroups = useMemoR(() => {
    const order = ["Quoted Footnotes", "Tanach", "Mishnah", "Talmud", "Gemara", "Midrash", "Halacha", "Jewish Thought", "Kabbalah", "Chasidut", "Rebbe", "Commentary"];
    const byGroup = {};
    sources.forEach((src) => {
      const group = sourceGroupLabel(src);
      (byGroup[group] = byGroup[group] || []).push(src);
    });
    return Object.entries(byGroup)
      .sort(([a], [b]) => {
        const rank = (group) => group === "Additional Quoted Sources" ? 10000 : (order.indexOf(group) === -1 ? 999 : order.indexOf(group));
        return rank(a) - rank(b) || a.localeCompare(b);
      })
      .map(([group, items]) => ({ group, items }));
  }, [sources]);
  const activeSegmentRef = activeSegId || "";
  const activeSegment = sicha.segments.find((seg) => seg.id === activeSegmentRef || seg.ref === activeSegmentRef) || null;
  const effectiveActiveNs = useMemoR(() => uniqNumbers(activeNs.concat(
    activeSegment ? sources.filter((source) => sourceMatchesSegment(source, activeSegment)).map((source) => source.n) : []
  )), [activeNs.join("|"), activeSegmentRef, sources]);
  const activeSet = useMemoR(() => new Set(effectiveActiveNs), [effectiveActiveNs.join("|")]);
  const activeSourceGroups = useMemoR(() => {
    if (!effectiveActiveNs.length) return [];
    return sourceGroups
      .map(({ group, items }) => ({
        group,
        items: items.filter((src) => activeSet.has(src.n)),
      }))
      .filter(({ items }) => items.length > 0);
  }, [sourceGroups, effectiveActiveNs.join("|")]);
  const openSourceSet = useMemoR(() => {
    const closed = new Set(closedSourceNs);
    const defaults = sources
      .filter((source) => !closed.has(source.n) && (
        (!source.footnote && activeSet.has(source.n))
        || (activeSet.has(source.n) && (fnStyle === "margin" || openGroups["Quoted Footnotes"]))
      ))
      .map((source) => source.n);
    return new Set(uniqNumbers(defaults.concat([openN])));
  }, [sources, effectiveActiveNs.join("|"), closedSourceNs.join("|"), openN, fnStyle, openGroups["Quoted Footnotes"]]);
  const activeSegIndex = activeSegment ? sicha.segments.findIndex((seg) => seg === activeSegment) : -1;
  const bookmarkRef = activeSegmentRef || sicha._liveRef || sicha.id;
  const bookmarked = bookmarks.includes(bookmarkRef);
  const selectedSource = sources.find((s) => s.n === openN) || null;
  const betaFootnotes = (betaRevision >= 0 && Array.isArray(window.BETA_FOOTNOTES) ? window.BETA_FOOTNOTES : [])
    .filter((item) => !effectiveActiveNs.length || effectiveActiveNs.includes(Number(item.sourceN)));
  useEffectR(() => {
    const onBeta = () => setBetaRevision((value) => value + 1);
    window.addEventListener("mayanos-beta-footnotes", onBeta);
    return () => window.removeEventListener("mayanos-beta-footnotes", onBeta);
  }, [sicha.id]);
  useEffectR(() => {
    if (selectedSource && selectedSource.footnote && window.loadBetaFootnotes) window.loadBetaFootnotes(selectedSource);
  }, [selectedSource && selectedSource.id]);
  const sectionStillLoaded = (sicha._loadedSections || []).some(section => [section.ref, section.targetRef, section.canonicalRef].includes(activeSectionRef));
  const currentTextRef = (sectionStillLoaded && activeSectionRef) || sicha._liveTargetRef || sicha._liveRef || sicha.id || sicha.titleEn;
  const activeSectionInfo = ((sicha._loadedSections || []).find((section) => (section.targetRef || section.ref) === currentTextRef || section.ref === currentTextRef)) || {
    ref: currentTextRef,
    targetRef: currentTextRef,
    prev: sicha.prev || null,
    next: sicha.next || null,
  };
  const canonicalCurrentTextRef = activeSectionInfo.canonicalRef || activeSectionInfo.ref || currentTextRef;
  const returnOrigin = (history.state && history.state.mayanosOrigin) || window.MAYANOS_RETURN_ORIGIN || null;
  const returnLabel = returnOrigin && (returnOrigin.returnLabel || returnOrigin.title || returnOrigin.ref) || "";
  const [sourceNotice, setSourceNotice] = useStateR("");
  useEffectR(() => {
    const onNotice = event => setSourceNotice(event.detail?.message || "");
    window.addEventListener("mayanos-source-notice", onNotice);
    return () => window.removeEventListener("mayanos-source-notice", onNotice);
  }, []);
  const sefariaTextUrl = canonicalCurrentTextRef ? `https://www.sefaria.org/${encodeURIComponent(String(canonicalCurrentTextRef).replace(/ /g, "_"))}` : "";
  const correctionUrl = canonicalCurrentTextRef ? `https://github.com/yisroel-w/Sefaria-Chassidus/issues/new?${new URLSearchParams({
    title: `Text correction: ${activeSegmentRef || canonicalCurrentTextRef}`,
    body: `Reference: ${activeSegmentRef || canonicalCurrentTextRef}\nMayanos URL: ${window.location.href}\n\nWhat appears incorrect?\n\nSuggested correction or source:\n`,
  }).toString()}` : "";
  const tocSections = (toc && Array.isArray(toc.sections)) ? toc.sections : [];
  const derivedTitleSections = useMemoR(() => {
    const baseRef = sicha._liveRef || sicha.id || "";
    if (!baseRef || !Array.isArray(sicha.segments)) return [];
    return sicha.segments
      .map((seg, i) => titleSectionFromSegment(seg, i, baseRef))
      .filter(Boolean);
  }, [sicha._liveRef, sicha.id, sicha.segments.length]);
  const displayTocSections = useMemoR(() => {
    if (!derivedTitleSections.length) return tocSections;
    const byN = {};
    derivedTitleSections.forEach((s) => { if (Number.isFinite(s.n)) byN[s.n] = s; });
    return tocSections.map((section) => {
      if (!section || section.isHeader) return section;
      const n = parseInt(String(section.label || section.ref || "").match(/(\d+)(?!.*\d)/)?.[1] || "", 10);
      const title = byN[n];
      if (!title) return section;
      return Object.assign({}, section, {
        label: title.label,
        heLabel: title.heLabel || section.heLabel,
        ref: section.ref || title.ref,
        named: true,
      });
    });
  }, [tocSections, derivedTitleSections]);
  const currentTitleSection = useMemoR(() => {
    if (!derivedTitleSections.length || activeSegIndex < 0) return null;
    let cur = null;
    derivedTitleSections.forEach((section) => {
      if (section.startIndex <= activeSegIndex) cur = section;
    });
    return cur;
  }, [derivedTitleSections, activeSegIndex]);
  const currentRefSection = useMemoR(() => {
    if (!displayTocSections.length || !currentTextRef) return null;
    const norm = (v) => String(v || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
    const currentNorm = norm(currentTextRef);
    const liveRef = norm(sicha._liveRef || sicha._readerRef || "");
    const exact = displayTocSections.find((section) => {
      if (!section || section.isHeader) return false;
      const refs = [section.targetRef, section.ref, section.readerRef].map(norm).filter(Boolean);
      return refs.includes(currentNorm);
    });
    if (exact) return exact;
    // Numeric tocTargetRefs such as `1500370001:maamar:35:seif:1` are not
    // globally unique. Never fall back to the final number (which made
    // Maamar 35 resolve to whichever other Section 1 appeared first).
    const semanticRef = liveRef || currentNorm;
    const maamar = semanticRef.match(/(?:maamar|sicha)\s+(\d+)/i)?.[1] || "";
    const seif = semanticRef.match(/(?:section|seif)\s+(\d+)/i)?.[1] || "";
    const volume = semanticRef.match(/\bvol(?:ume)?\.?\s*(\d+)/i)?.[1] || "";
    if (!maamar || !seif) return null;
    const candidates = displayTocSections.filter((section) => {
      if (!section || section.isHeader) return false;
      const sectionRef = norm(section.ref || section.targetRef || section.readerRef || "");
      const sectionMaamar = sectionRef.match(/(?:maamar|sicha)\s+(\d+)/i)?.[1] || "";
      const sectionSeif = sectionRef.match(/(?:section|seif)\s+(\d+)/i)?.[1] || "";
      const sectionVolume = sectionRef.match(/\bvol(?:ume)?\.?\s*(\d+)/i)?.[1] || "";
      return sectionMaamar === maamar && sectionSeif === seif && (!volume || sectionVolume === volume);
    });
    return candidates.length === 1 ? candidates[0] : null;
  }, [displayTocSections, currentTextRef, sicha._liveRef, sicha._readerRef]);
  // A maamar is one reading unit: fetchReadingUnit already assembles every seif
  // of it. The ref that follows the last seif belongs to the *next* maamar, so
  // continuous loading stops here and the reader offers it as a named step.
  const maamarUnit = useMemoR(
    () => /\bmaamar\s+\d+/i.test(String(sicha._liveRef || sicha._readerRef || sicha.id || "")),
    [sicha._liveRef, sicha._readerRef, sicha.id]);
  const maamarContinuation = useMemoR(() => {
    if (!maamarUnit || !sicha.next) return null;
    const nextRef = String(sicha.next);
    const norm = (value) => String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
    const refsOf = (section) => [section.targetRef, section.readerRef, section.ref].map(norm).filter(Boolean);
    const target = norm(nextRef);
    const maamarNumber = nextRef.match(/(?:maamar|sicha)[\s:]+(\d+)/i)?.[1] || "";
    // The contents outline lists the discourse row ("Maamar 36 · יו״ד שבט תשי״ב")
    // as well as seif rows that can share its ref, so always prefer the row that
    // names the maamar over one that only says "סעיף א".
    const namesDiscourse = (section) => maamarNumber
      ? new RegExp(`^maamar\\s+${maamarNumber}\\b`, "i").test(String(section.label || "").trim())
      : false;
    const byRef = displayTocSections.filter((section) => section && !section.isHeader && refsOf(section).includes(target));
    let byNumber = [];
    if (maamarNumber) {
      // Melukat's table of contents spans several volumes, so keep the label
      // lookup inside the volume the reader is actually in.
      const liveVolume = String(sicha._liveRef || sicha._readerRef || "").match(/\bvol(?:ume)?\.?\s*(\d+)/i)?.[1] || "";
      const wanted = new RegExp(`\\bmaamar\\s+${maamarNumber}\\b`, "i");
      const volume = liveVolume ? new RegExp(`\\bvol(?:ume)?\\.?\\s*${liveVolume}\\b`, "i") : null;
      byNumber = displayTocSections.filter((section) => section && !section.isHeader
        && wanted.test(String(section.ref || section.targetRef || section.readerRef || ""))
        && (!volume || volume.test(String(section.ref || section.targetRef || section.readerRef || ""))));
    }
    const candidates = byNumber.length ? byNumber.concat(byRef) : byRef;
    // Only accept a row that actually names a maamar. Section rows carry the
    // same ref and would otherwise label the next step "Section 1".
    const row = candidates.find(namesDiscourse)
      || candidates.find((section) => /maamar\s+\d+/i.test(String(section.label || "")));
    const presented = row ? tocPresentation(row) : null;
    return {
      ref: nextRef,
      title: (presented && presented.title) || (maamarNumber ? `Maamar ${maamarNumber}` : nextRef),
      heTitle: (presented && presented.heTitle) || "",
    };
  }, [maamarUnit, sicha.next, sicha._liveRef, sicha._readerRef, displayTocSections]);
  const readerLocationSection = currentTitleSection || currentRefSection;
  const readerCollectionLabel = useMemoR(() => {
    const parts = [sicha.collection, sicha.volume, sicha.parsha]
      .map((part) => String(part || "").trim())
      .filter(Boolean);
    const kept = [];
    parts.forEach((part) => {
      const lower = part.toLowerCase();
      if (kept.some((existing) => existing.toLowerCase().includes(lower))) return;
      const containedIndex = kept.findIndex((existing) => lower.includes(existing.toLowerCase()));
      if (containedIndex >= 0) kept[containedIndex] = part;
      else kept.push(part);
    });
    return kept.join(" · ") || currentTextRef;
  }, [sicha.collection, sicha.volume, sicha.parsha, currentTextRef]);
  const rawLocationTitle = readerLocationSection?.label || sicha.titleEn || currentTextRef;
  const discourseLocation = String(activeSectionInfo.readerRef || activeSectionInfo.canonicalRef || currentTextRef || sicha._readerRef || "").match(/\b(?:Sicha|Maamar)\s+\d+/i)?.[0];
  const readerLocationTitle = discourseLocation && !/\b(?:Sicha|Maamar)\s+\d+/i.test(rawLocationTitle)
    ? `${discourseLocation}, ${rawLocationTitle}` : rawLocationTitle;
  const readerLocationHe = readerLocationSection && tocHasDistinctHe(readerLocationSection) ? readerLocationSection.heLabel : "";
  const readerCollectionHeLabel = String(sicha.heCollection || "").trim();
  const prepareTextForPrint = async () => {
    if (printPreparing || sicha._gathering) return;
    if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("print_started", { surface: "reader", stage: "setup" });
    setShareOpen(false);
    setSettingsOpen(false);
    setPrintTargetRef(currentTextRef);
    setPrintPreparing(true);
    try {
      const normalizeRef = (value) => String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
      const currentRow = displayTocSections.find(section => section && !section.isHeader
        && [section.ref, section.targetRef, section.readerRef].some(ref => normalizeRef(ref) === normalizeRef(currentTextRef)));
      const siblingSections = currentRow ? discourseSections({ sections: displayTocSections }, {
        ref: currentRow.ref, readerRef: currentRow.readerRef, tocTargetRef: currentRow.targetRef,
      }) : [];
      const wantedRefs = new Set();
      siblingSections.forEach((section) => {
        [section.ref, section.targetRef, section.readerRef].forEach((ref) => {
          const normalized = normalizeRef(ref);
          if (normalized) wantedRefs.add(normalized);
        });
      });
      const maxLoads = Math.max(1, siblingSections.length + 2);
      const loadWorkBoundary = async (direction, loader) => {
        if (!wantedRefs.size) return;
        let loads = 0;
        while (window.SICHA && window.SICHA[direction] && loads < maxLoads) {
          // Stop at the first section outside this Sicha/Maamer. If a legacy
          // TOC has no usable siblings, keep the current loaded range safe.
          if (wantedRefs.size && !wantedRefs.has(normalizeRef(window.SICHA[direction]))) break;
          if (!loader || !(await loader())) break;
          loads += 1;
        }
      };
      await loadWorkBoundary("prev", window.loadPrevious);
      await loadWorkBoundary("next", window.loadMore);
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      setPrintOpen(true);
    } finally {
      setPrintPreparing(false);
    }
  };
  useEffectR(() => {
    nextLoadStateRef.current = "";
    setReaderScrollTop(0);
    setNextLoadState("");
    lastDocumentScrollTopRef.current = 0;
    showFullMobileChrome();
    if (isMobile) setSheetOpen(false);
    setActiveSectionRef(sicha._liveTargetRef || sicha._liveRef || sicha.id || "");
    setActiveSegId(null);
    setOpenN(null);
    setFocusedMarkerN(null);
    setOpenGroups({});
    setClosedSourceNs([]);
    setFnInline([]);
    setInlineOpenAll(false);
    setPrintOpts(null);
  }, [sicha.id, isMobile]);

  // Keep the reader continuous: fetch the next complete discourse as the
  // current text approaches the end. The controller survives effect cleanup
  // when appended content changes the dependencies during an in-flight fetch.
  useEffectR(() => {
    const controller = continuationControllerRef.current;
    const key = `${sicha.id || ""}\u0000${sicha.next || ""}`;
    const changedKey = controller.key !== null && controller.key !== key;
    controller.reset(key);
    if (changedKey) {
      nextLoadStateRef.current = "";
      setNextLoadState("");
    }
    // A maamar is one reading unit. Its sections are assembled by
    // fetchReadingUnit; the adjacent ref after that belongs to a new maamar,
    // so it is offered as an explicit next step instead of being appended.
    if (maamarUnit || sicha._sourceView || !sicha.next || controller.status === "error") return;
    const maybeLoadNext = async () => {
      if (!sicha.next || controller.key !== key || controller.status !== "idle") return;
      const remaining = readerScrollHeight() - readerScrollTop() - readerViewportHeight();
      if (remaining > Math.max(600, readerViewportHeight() * 1.25)) return;
      nextLoadStateRef.current = "loading";
      setNextLoadState("loading");
      const loaded = await controller.request(key);
      if (controller.key !== key) return;
      nextLoadStateRef.current = loaded ? "" : "error";
      setNextLoadState(nextLoadStateRef.current);
    };
    const target = isMobile ? window : scrollRef.current;
    target?.addEventListener("scroll", maybeLoadNext, { passive: true });
    requestAnimationFrame(maybeLoadNext);
    return () => {
      target?.removeEventListener("scroll", maybeLoadNext);
    };
  }, [sicha.id, sicha.next, sicha.segments.length, isMobile, maamarUnit]);

  useEffectR(() => {
    const root = document.documentElement;
    root.dataset.readerScroll = "document";
    root.dataset.readerChrome = sheetOpen ? "compact" : (isMobile ? mobileChromeMode : "full");
    return () => {
      delete root.dataset.readerScroll;
      delete root.dataset.readerChrome;
    };
  }, [isMobile, sheetOpen, mobileChromeMode]);

  useEffectR(() => {
    const markReaderInput = () => { lastReaderInputRef.current = Date.now(); };
    window.addEventListener("touchmove", markReaderInput, { passive: true });
    window.addEventListener("wheel", markReaderInput, { passive: true });
    return () => {
      window.removeEventListener("touchmove", markReaderInput);
      window.removeEventListener("wheel", markReaderInput);
    };
  }, []);

  useEffectR(() => {
    if (!isMobile || sheetAnchorScrollRef.current == null) return;
    const top = sheetAnchorScrollRef.current;
    sheetAnchorScrollRef.current = null;
    const restore = () => {
      lastDocumentScrollTopRef.current = top;
      window.scrollTo({ top, behavior: "auto" });
      const scrollingElement = document.scrollingElement;
      if (scrollingElement) scrollingElement.scrollTop = top;
      document.documentElement.scrollTop = top;
      if (document.body) document.body.scrollTop = top;
    };
    requestAnimationFrame(() => requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    }));
  }, [isMobile, sheetOpen]);

  useEffectR(() => () => {
    if (isMobile) window.scrollTo({ top: 0, behavior: "auto" });
  }, [isMobile]);

  useEffectR(() => {
    const openPrintSetup = () => {
      prepareTextForPrint();
    };
    window.addEventListener("mayanos-open-print", openPrintSetup);
    return () => window.removeEventListener("mayanos-open-print", openPrintSetup);
  }, [printPreparing, currentTextRef, displayTocSections]);

  useEffectR(() => {
    window.__mayanosReaderLocation = {
      sectionRef: currentTextRef,
      segmentRef: activeSegmentRef,
      scrollTop: readerScrollTop(),
      title: readerLocationTitle || currentTextRef,
    };
    window.__mayanosCaptureReaderLocation = () => ({ ...window.__mayanosReaderLocation, scrollTop: readerScrollTop() });
    return () => { delete window.__mayanosCaptureReaderLocation; };
  }, [currentTextRef, activeSegmentRef, readerLocationTitle, isMobile]);

  useEffectR(() => {
    const scrollEl = scrollRef.current;
    const headEl = readHeadRef.current;
    if (!scrollEl || !headEl) return;
    const sync = () => {
      scrollEl.style.setProperty("--read-head-h", `${Math.ceil(headEl.getBoundingClientRect().height)}px`);
    };
    sync();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(sync) : null;
    if (ro) ro.observe(headEl);
    window.addEventListener("resize", sync);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [readerLocationTitle, readerLocationHe, isMobile]);
  const tocEntries = useMemoR(() => displayTocSections.map((section, index) => Object.assign(tocPresentation(section), { tocIndex: index })), [displayTocSections]);
  const tocGroups = useMemoR(() => {
    const groups = [];
    const headerStack = [];
    let group = null;
    tocEntries.forEach((entry) => {
      if (entry.isHeader) {
        const depth = Number.isFinite(Number(entry.depth)) ? Number(entry.depth) : 0;
        while (headerStack.length && headerStack[headerStack.length - 1].depth >= depth) headerStack.pop();
        const ownPath = (Array.isArray(entry.path) && entry.path.length ? entry.path : String(entry.title || entry.label || "").split(/\s*·\s*/))
          .map((part) => String(part || "").trim()).filter(Boolean);
        const path = ownPath.length > 1 ? ownPath : [...headerStack.map((item) => item.label), ...(ownPath.length ? ownPath : [entry.title || entry.label || "Contents"])];
        group = {
          id: `toc-group-${entry.tocIndex}`,
          ref: entry.ref,
          label: entry.title || entry.label || "Contents",
          heLabel: entry.heTitle || entry.heLabel || "",
          path: path.length ? path : [entry.title || entry.label || "Contents"],
          items: [],
        };
        groups.push(group);
        headerStack.push({ depth, label: ownPath.at(-1) || entry.title || entry.label || "Contents" });
        return;
      }
      if (!group) {
        group = { id: "toc-group-root", ref: entry.ref, label: toc && toc.book || "Contents", heLabel: "", path: [toc && toc.book || "Contents"], items: [] };
        groups.push(group);
      }
      group.items.push(entry);
    });
    return groups.filter((item) => item.items.length);
  }, [toc && toc.book, tocEntries]);
  const tocPrimaryGroups = useMemoR(() => {
    const primaries = [];
    const byKey = {};
    tocGroups.forEach((group) => {
      const label = group.path[0] || group.label;
      const key = tocNormalize(label) || group.id;
      if (!byKey[key]) {
        byKey[key] = { key, label, groups: [], sectionCount: 0 };
        primaries.push(byKey[key]);
      }
      const secondary = group.path.length > 1 ? group.path.slice(1).join(" · ") : group.label;
      byKey[key].groups.push(Object.assign({}, group, { secondary }));
      byKey[key].sectionCount += group.items.length;
    });
    return primaries;
  }, [tocGroups]);
  const currentTocEntry = useMemoR(() => {
    const current = tocNormalize(currentTextRef);
    const known = currentRefSection && tocNormalize(currentRefSection.ref);
    return tocEntries.find((entry) => !entry.isHeader && (
      tocNormalize(entry.targetRef) === current || tocNormalize(entry.ref) === current ||
      (known && tocNormalize(entry.ref) === known)
    )) || null;
  }, [tocEntries, currentTextRef, currentRefSection && currentRefSection.ref]);
  const currentTocPrimary = useMemoR(() => tocPrimaryGroups.find((primary) => primary.groups.some((group) => group.items.some((item) => currentTocEntry && item.tocIndex === currentTocEntry.tocIndex))) || null, [tocPrimaryGroups, currentTocEntry]);
  const selectedTocPrimary = tocPrimaryGroups.find((primary) => primary.key === tocBrowseKey) || currentTocPrimary || tocPrimaryGroups[0] || null;
  const tocSearchResults = useMemoR(() => {
    const query = tocNormalize(tocQ);
    if (!query) return [];
    const tokens = query.split(" ").filter(Boolean);
    const sichaIntent = query.match(/(?:^| )sicha (\d+)(?: |$)/);
    const unitIntent = query.match(/(?:^| )(section|chapter|letter|maamar) (\d+)(?: |$)/);
    const rows = [];
    tocPrimaryGroups.forEach((primary, primaryIndex) => primary.groups.forEach((group, groupIndex) => group.items.forEach((entry) => {
      const shortTitle = tocShortSectionTitle(entry, group.secondary);
      const groupTitle = tocNormalize(group.secondary);
      const unitTitle = tocNormalize(shortTitle);
      if (sichaIntent && !new RegExp(`(?:^| )sicha ${sichaIntent[1]}(?: |$)`).test(groupTitle)) return;
      if (unitIntent && !new RegExp(`(?:^| )${unitIntent[1]} ${unitIntent[2]}(?: |$)`).test(unitTitle)) return;
      const searchable = tocNormalize([
        primary.label, group.label, group.secondary, group.heLabel, entry.title, entry.heTitle,
        entry.excerpt, entry.heExcerpt, entry.ref,
      ].join(" "));
      if (!tokens.every((token) => searchable.includes(token))) return;
      const title = tocNormalize(shortTitle);
      const fullTitle = tocNormalize(entry.title);
      let score = 0;
      if (title === query || fullTitle === query) score += 500;
      if (title.startsWith(query) || fullTitle.startsWith(query)) score += 220;
      if (searchable.includes(query)) score += 140;
      if (primary.key === (currentTocPrimary && currentTocPrimary.key)) score += 20;
      if (currentTocEntry && entry.tocIndex === currentTocEntry.tocIndex) score += 30;
      rows.push({ entry, group, primary, shortTitle, score, order: primaryIndex * 10000 + groupIndex * 1000 + entry.tocIndex });
    })));
    return rows.sort((a, b) => b.score - a.score || a.order - b.order);
  }, [tocQ, tocPrimaryGroups, currentTocPrimary && currentTocPrimary.key, currentTocEntry && currentTocEntry.tocIndex]);
  const tocSearchMode = !!tocNormalize(tocQ);
  const resourceBuckets = useMemoR(() => {
    const order = ["topic", "sheet", "webpage", "note", "manuscript", "media", "guide"];
    const byKind = {};
    relatedResources.filter(resource => topicsVisible || resource.kind !== "topic").forEach((resource) => {
      (byKind[resource.kind] = byKind[resource.kind] || []).push(resource);
    });
    return order
      .filter((kind) => byKind[kind] && byKind[kind].length)
      .map((kind) => ({ kind, label: resourceKindLabel(kind), resources: byKind[kind] }));
  }, [relatedResources, sicha.id, topicsVisible]);
  const versionCounts = useMemoR(() => {
    const v = sicha.versionsAvailable || {};
    return { he: (v.he || []).length, en: (v.en || []).length, total: (v.he || []).length + (v.en || []).length };
  }, [sicha.id]);
  const searchResults = useMemoR(() => {
    const q = searchQ.trim().toLowerCase();
    if (q.length < 2) return [];
    const local = sicha.segments
      .map((seg, index) => ({
        index,
        ref: seg.ref,
        he: String(seg.he || "").replace(/‹\d+›/g, ""),
        en: String(seg.en || "").replace(/‹\d+›/g, ""),
      }))
      .filter((seg) => (seg.he + " " + seg.en).toLowerCase().includes(q))
      .slice(0, 24);
    const seen = new Set(local.map((result) => result.ref));
    return local.concat((bookSearchResults || []).filter((result) => result.ref && !seen.has(result.ref)).map((result) => ({
      index: -1,
      ref: result.ref,
      he: result.language === "he" ? result.excerpt || "" : "",
      en: result.language === "he" ? "" : result.excerpt || "",
      remote: true,
    }))).slice(0, 100);
  }, [searchQ, sicha.id, sicha.segments.length, bookSearchResults]);
  const activeSearchQuery = (tab === "sources" && railTool === "search" && searchQ.trim().length >= 2) ? searchQ.trim() : "";
  const currentSearchIndex = searchResults.length ? Math.min(searchActiveIndex, searchResults.length - 1) : -1;

  useEffectR(() => { setSearchActiveIndex(0); }, [searchQ, sicha.id]);
  useEffectR(() => {
    const query = searchQ.trim();
    if (query.length < 2 || !sicha.collection) { setBookSearchResults([]); setBookSearchLoading(false); return; }
    let cancelled = false;
    setBookSearchLoading(true);
    setBookSearchError("");
    const controller = new AbortController();
    const timer = setTimeout(() => fetch(`/bff/search?q=${encodeURIComponent(query)}&book=${encodeURIComponent(sicha.collection)}&limit=100`, { signal: controller.signal, headers: { Accept: "application/json" } })
      .then((response) => { if (!response.ok) throw new Error("Search unavailable"); return response.json(); })
      .then((results) => { if (!cancelled) setBookSearchResults(Array.isArray(results) ? results : []); })
      .catch((error) => { if (!cancelled && error && error.name !== "AbortError") { setBookSearchResults([]); setBookSearchError("Search is temporarily unavailable."); } })
      .finally(() => { if (!cancelled) setBookSearchLoading(false); }), 220);
    return () => { cancelled = true; clearTimeout(timer); controller.abort(); };
  }, [searchQ, sicha.collection, sicha.id, bookSearchRetry]);
  useEffectR(() => {
    if (searchResults.length && searchActiveIndex >= searchResults.length) setSearchActiveIndex(searchResults.length - 1);
  }, [searchResults.length, searchActiveIndex]);

  useEffectR(() => {
    const loadBookmarks = () => { try {
      const saved = JSON.parse(localStorage.getItem("mayanos-reader-bookmarks") || "[]");
      if (Array.isArray(saved)) setBookmarks(saved.filter((x) => typeof x === "string"));
    } catch (e) {} };
    loadBookmarks();
    window.addEventListener("mayanos-account", loadBookmarks);
    return () => window.removeEventListener("mayanos-account", loadBookmarks);
  }, []);

  const toggleBookmark = () => {
    if (!bookmarkRef) return;
    setBookmarks((prev) => {
      const next = prev.includes(bookmarkRef) ? prev.filter((x) => x !== bookmarkRef) : [bookmarkRef].concat(prev).slice(0, 250);
      try { localStorage.setItem("mayanos-reader-bookmarks", JSON.stringify(next)); } catch (e) {}
      try { window.dispatchEvent(new Event("mayanos-bookmarks")); } catch (e) {}
      if (window.MAYANOS_ACCOUNT_SYNC) window.MAYANOS_ACCOUNT_SYNC.schedule();
      if (!prev.includes(bookmarkRef)) try { window.dispatchEvent(new CustomEvent("mayanos-account-prompt", { detail: { reason: "bookmark" } })); } catch (e) {}
      return next;
    });
  };

  const removeBookmark = (ref) => {
    setBookmarks((prev) => {
      const next = prev.filter((x) => x !== ref);
      try { localStorage.setItem("mayanos-reader-bookmarks", JSON.stringify(next)); } catch (e) {}
      try { window.dispatchEvent(new Event("mayanos-bookmarks")); } catch (e) {}
      if (window.MAYANOS_ACCOUNT_SYNC) window.MAYANOS_ACCOUNT_SYNC.schedule();
      return next;
    });
  };

  const clearBookmarks = () => {
    setBookmarks([]);
    try { localStorage.setItem("mayanos-reader-bookmarks", "[]"); } catch (e) {}
    try { window.dispatchEvent(new Event("mayanos-bookmarks")); } catch (e) {}
    if (window.MAYANOS_ACCOUNT_SYNC) window.MAYANOS_ACCOUNT_SYNC.schedule();
  };

  const openRail = (nextTab, nextTool) => {
    const targetTab = nextTab || "sources";
    const targetTool = nextTool || null;
    const alreadyOpen = isMobile
      ? sheetOpen && tab === targetTab && (railTool || null) === targetTool
      : !railCollapsed && tab === targetTab && (railTool || null) === targetTool && !showGraph;
    if (alreadyOpen) {
      if (isMobile) closeMobileSheet();
      else setRailCollapsed(true);
      return;
    }
    setTab(targetTab);
    setRailTool(targetTool);
    setShowGraph(false);
    setRailCollapsed(false);
    setShareOpen(false);
    setSettingsOpen(false);
    if (window.MAYANOS_ANALYTICS) {
      window.MAYANOS_ANALYTICS.track("sources_panel_opened", {
        tab: targetTab,
        tool: targetTool || "",
        mobile: !!isMobile,
      });
    }
    if (isMobile) {
      openMobileSheet();
      setTimeout(() => setUsefulSheetHeight(targetTool === "search" ? 0.84 : 0.4), 0);
    }
  };

  const openDictionaryPanel = (query = "") => {
    setShareOpen(false);
    setSettingsOpen(false);
    setDictionaryQuery(query || "");
    setDictionaryPeek(null);
    setTab("dictionary");
    setRailTool(null);
    setShowGraph(false);
    setRailCollapsed(false);
    if (isMobile) openMobileSheet();
  };

  useEffectR(() => {
    if (!window.MAYANOS_DICTIONARY) return undefined;
    return window.MAYANOS_DICTIONARY.subscribe(() => setDictionaryRevision((value) => value + 1));
  }, []);

  // A selection on desktop, or a tap on one word on mobile, opens a quiet
  // contextual lookup. The full history/saved list remains in the Reader rail.
  useEffectR(() => {
    const positionFor = (rect) => {
      const width = Math.min(360, window.innerWidth - 24);
      return { top: Math.min(window.innerHeight - 220, Math.max(14, rect.bottom + 10)), left: Math.min(window.innerWidth - width / 2 - 12, Math.max(width / 2 + 12, rect.left + rect.width / 2)) };
    };
    const onPointerDown = (event) => {
      const inDictionary = event.target && event.target.closest && event.target.closest(".dictionary-popover-wrap");
      if (inDictionary) {
        dictionaryKeepOpenRef.current = true;
        return;
      }
      if (dictionaryPeek) {
        dictionaryKeepOpenRef.current = false;
        setDictionaryPeek(null);
      }
    };
    const onPointerUp = (event) => {
      // Toolbar and sheet controls have their own click handlers. Do not let
      // the document-level mobile tap router reinterpret those clicks as a
      // request to open the Sources sheet (this made the A/settings control
      // appear to jump the reader and stack panels).
      const interactive = event.target && event.target.closest && event.target.closest("button, a, input, textarea, select, [role=button]");
      if (interactive && !(interactive.closest && interactive.closest(".seg"))) return;
      const target = event.target && event.target.closest ? event.target.closest(".read-body") : null;
      if (!target) {
        if (isMobile && event.target && event.target.closest && event.target.closest(".read-scroll, .read-head")) openRail("sources");
        return;
      }
      if (event.target.closest && event.target.closest(".fnmark, .fninline-text")) return;
      setTimeout(() => {
        const selection = window.getSelection && window.getSelection();
        const selectedText = selection && String(selection).trim();
        if (selectedText) {
          const range = selection.rangeCount ? selection.getRangeAt(0) : null;
          if (!range || !target.contains(range.commonAncestorContainer)) return;
          const query = readerDictionarySelection(selectedText);
          if (!query) {
            if (isMobile) openRail("sources");
            return;
          }
          if (selection && selection.removeAllRanges) selection.removeAllRanges();
          dictionaryKeepOpenRef.current = true;
          setDictionaryPeek({ query, pos: positionFor(range.getBoundingClientRect()) });
          return;
        }
        if (!isMobile) return;
        const hit = readerWordAtPoint(event);
        if (hit) {
          dictionaryKeepOpenRef.current = true;
          setDictionaryPeek({ query: hit.query, pos: positionFor(hit.rect) });
        } else openRail("sources");
      }, 0);
    };
    const onScroll = () => { if (!dictionaryKeepOpenRef.current) setDictionaryPeek(null); };
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointerdown", onPointerDown);
    const scrollTarget = isMobile ? window : scrollRef.current;
    if (scrollTarget) scrollTarget.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointerdown", onPointerDown);
      if (scrollTarget) scrollTarget.removeEventListener("scroll", onScroll);
    };
  }, [isMobile, sicha.id, dictionaryPeek]);

  const toggleReaderSettings = () => {
    if (isMobile && sheetOpen) closeMobileSheet();
    setShareOpen(false);
    setSettingsOpen((open) => !open);
  };

  useEffectR(() => {
    if (tab !== "sources" || railTool !== "search") return;
    const id = setTimeout(() => searchInputRef.current && searchInputRef.current.focus(), 80);
    return () => clearTimeout(id);
  }, [tab, railTool]);

  // A chelek opened from the library asks for the letter chooser, so the reader
  // never silently drops someone into the first maamar of the volume.
  useEffectR(() => {
    const onOpenContents = () => { if (window.__mayanosTOC) setTocOpen(true); };
    window.addEventListener("mayanos-open-contents", onOpenContents);
    return () => window.removeEventListener("mayanos-open-contents", onOpenContents);
  }, []);

  useEffectR(() => {
    if (!tocOpen) return;
    if (currentTocPrimary) setTocBrowseKey(currentTocPrimary.key);
    const focusId = setTimeout(() => {
      if (tocOverlayInputRef.current) tocOverlayInputRef.current.focus();
      const current = document.querySelector(".toc-panel .toc-section-row.on");
      if (current && current.scrollIntoView) current.scrollIntoView({ block: "center" });
    }, 100);
    return () => clearTimeout(focusId);
  }, [tocOpen]);

  useEffectR(() => {
    setTocSearchActiveIndex(0);
  }, [tocQ]);

  useEffectR(() => {
    if (!tocOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setTocOpen(false);
      } else if (tocSearchMode && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        e.preventDefault();
        setTocSearchActiveIndex((value) => {
          const count = tocSearchResults.length;
          if (!count) return 0;
          return e.key === "ArrowDown" ? (value + 1) % count : (value - 1 + count) % count;
        });
      } else if (tocSearchMode && e.key === "Enter" && tocSearchResults.length) {
        e.preventDefault();
        const row = tocSearchResults[Math.min(tocSearchActiveIndex, tocSearchResults.length - 1)];
        if (row && row.entry && (row.entry.targetRef || row.entry.ref)) {
          setTocOpen(false);
          window.loadSicha && window.loadSicha(row.entry.targetRef || row.entry.ref);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tocOpen, tocSearchMode, tocSearchResults, tocSearchActiveIndex]);

  // The secondary actions close on Escape or a click outside the menu.
  useEffectR(() => {
    if (!moreOpen) return;
    const onKey = (event) => { if (event.key === "Escape") closeMore(); };
    const onDown = (event) => {
      const node = moreMenuRef.current;
      if (node && node.contains(event.target)) return;
      if (event.target && event.target.closest && event.target.closest(".read-head-actions")) return;
      closeMore();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [moreOpen]);

  // j / k walk sections, the keyboard equivalent of the previous/next buttons.
  useEffectR(() => {
    const onKey = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target && (/input|textarea|select/i.test(target.tagName || "") || target.isContentEditable)) return;
      if (event.key !== "j" && event.key !== "k") return;
      const nextRef = event.key === "j" ? activeSectionInfo.next : activeSectionInfo.prev;
      if (!nextRef) return;
      event.preventDefault();
      jumpSection(nextRef);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeSectionInfo.next, activeSectionInfo.prev]);

  const clearSearch = () => {
    setSearchQ("");
    setTimeout(() => searchInputRef.current && searchInputRef.current.focus(), 0);
  };

  useEffectR(() => {
    const typingTarget = (node) => {
      const tag = (node && node.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" || (node && node.isContentEditable);
    };
    const onReaderKey = (e) => {
      if (typingTarget(e.target)) return;
      const key = e.key;
      const meta = e.metaKey || e.ctrlKey;
      const claim = () => {
        e.preventDefault();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        else e.stopPropagation();
      };
      if (key === "Escape") {
        if (shareOpen || settingsOpen) {
          claim();
          setShareOpen(false);
          setSettingsOpen(false);
        }
        return;
      }
      if (key === "/" || (meta && key.toLowerCase() === "f")) {
        claim();
        openRail("sources", "search");
        return;
      }
      if (meta || e.altKey) return;
      if (key.toLowerCase() === "t" && toc) {
        claim();
        setTocOpen(true);
        setShareOpen(false);
        setSettingsOpen(false);
      } else if (key.toLowerCase() === "r") {
        claim();
        openRail("sources");
      } else if (key.toLowerCase() === "a") {
        claim();
        toggleReaderSettings();
      } else if (key.toLowerCase() === "l") {
        claim();
        setShareOpen((v) => !v);
        setSettingsOpen(false);
      } else if (key.toLowerCase() === "b") {
        claim();
        toggleBookmark();
      } else if (key.toLowerCase() === "p") {
        claim();
        prepareTextForPrint();
      }
    };
    window.addEventListener("keydown", onReaderKey, true);
    return () => window.removeEventListener("keydown", onReaderKey, true);
  }, [shareOpen, settingsOpen, toc, bookmarkRef, tab, railTool, showGraph, isMobile, printPreparing, currentTextRef, displayTocSections]);

  const toggleMapKey = (setter, key) => {
    setter((prev) => Object.assign({}, prev, { [key]: !prev[key] }));
  };

  const applyActiveSources = (ns) => {
    const next = uniqNumbers(ns || []);
    setActiveNs(next);
    return next;
  };

  const toggleSourceCard = (n) => {
    const isExpanded = openSourceSet.has(n);
    if (isExpanded) {
      setClosedSourceNs((prev) => uniqNumbers(prev.concat([n])));
      if (openN === n) setOpenN(null);
      return;
    }
    setClosedSourceNs((prev) => prev.filter((value) => value !== n));
    setOpenN(n);
    const source = sources.find((item) => item.n === n);
    if (source && source.anchorRef) setTimeout(() => focusSourceAnchor(source.anchorRef, source), 0);
  };

  const revealSourceInRail = (src) => {
    if (!src || !src.n) return;
    const group = sourceGroupLabel(src);
    manualSyncUntilRef.current = Date.now() + 1200;
    setTab("sources");
    setRailTool(null);
    setShowGraph(false);
    setRailCollapsed(false);
    setActiveNs((prev) => uniqNumbers(prev.concat([src.n])));
    setClosedSourceNs((prev) => prev.filter((n) => n !== src.n));
    setOpenN(src.n);
    setOpenGroups((prev) => Object.assign({}, prev, { [group]: true }));
    if (isMobile) {
      openMobileSheet();
      if (curSheetH < mobileViewportHeight() * 0.48) setTimeout(() => setUsefulSheetHeight(0.52), 0);
    }
    const scrollToCard = (tries = 0) => {
      const node = railRef.current && railRef.current.querySelector(`[data-src-n="${src.n}"]`);
      if (!node) {
        if (tries < 5) setTimeout(() => scrollToCard(tries + 1), 90);
        return;
      }
      let cont = railRef.current.querySelector(".rail-body");
      if (!cont || cont.scrollHeight <= cont.clientHeight + 4) cont = railRef.current;
      const nr = node.getBoundingClientRect(), cr = cont.getBoundingClientRect();
      cont.scrollTo({ top: cont.scrollTop + (nr.top - cr.top) - 18, behavior: tries ? "auto" : "smooth" });
      node.classList.remove("just-opened");
      void node.offsetWidth;
      node.classList.add("just-opened");
      setTimeout(() => node.classList.remove("just-opened"), 1600);
    };
    setTimeout(() => scrollToCard(), 120);
  };

  const openAllFootnotes = () => {
    const notes = sources.filter((source) => source.footnote && (!activeSegment || sourceMatchesSegment(source, activeSegment)));
    const noteNs = notes.map((source) => source.n);
    setTab("sources");
    setRailTool(null);
    setShowGraph(false);
    setRailCollapsed(false);
    setClosedSourceNs((previous) => previous.filter((n) => !noteNs.includes(n)));
    setOpenGroups((previous) => Object.assign({}, previous, { "Quoted Footnotes": true }));
    setExpandedSourceGroups((previous) => Object.assign({}, previous, { "Quoted Footnotes": true }));
    if (noteNs.length) setActiveNs((previous) => uniqNumbers(previous.concat(noteNs)));
    if (isMobile) openMobileSheet();
  };

  const syncActiveSegment = (ref) => {
    const idx = sicha.segments.findIndex((seg) => seg.ref === ref || seg.id === ref);
    if (idx < 0) return [];
    const seg = sicha.segments[idx];
    lastSegRef.current = idx;
    setActiveSegId(seg.id || seg.ref);
    if (seg._sectionRef) setActiveSectionRef(seg._sectionRef);
    const markerNs = ((String(seg.he) + " " + String(seg.en)).match(/‹(\d+)›/g) || []).map((x) => parseInt(x.replace(/[‹›]/g, ""), 10));
    const relatedNs = sources.filter((s) => sourceMatchesSegment(s, seg)).map((s) => s.n);
    const ns = uniqNumbers(markerNs.concat(relatedNs));
    return applyActiveSources(ns);
  };

  const selectSegment = (ref) => {
    if (!ref) return;
    if (window.getSelection && String(window.getSelection()).trim()) return;
    manualSyncUntilRef.current = Date.now() + 1200;
    syncActiveSegment(ref);
    setTab("sources");
    setRailTool(null);
    setShowGraph(false);
    setRailCollapsed(false);
    if (isMobile) {
      openMobileSheet();
      setTimeout(() => setUsefulSheetHeight(0.4), 0);
    }
  };

  const scrollToSegment = (ref, source) => {
    const el = scrollRef.current;
    if (!el || !ref) return false;
    const node = Array.from(el.querySelectorAll(".seg")).find((seg) => seg.getAttribute("data-seg-id") === ref || seg.getAttribute("data-seg-ref") === ref);
    if (node) {
      if (source && source.n) setOpenN(source.n);
      const markerN = Number(source && (source.originFootnoteN || (source.footnote ? source.n : 0))) || null;
      setFocusedMarkerN(markerN);
      syncActiveSegment(ref);
      manualSyncUntilRef.current = Date.now() + 1100;
      const marker = markerN ? node.querySelector(`[data-fn-n="${markerN}"]`) : null;
      const target = marker || node;
      const headHeight = readHeadRef.current ? Math.ceil(readHeadRef.current.getBoundingClientRect().height) : 0;
      const navHeight = isMobile ? Math.ceil(document.querySelector(".topnav")?.getBoundingClientRect().height || 0) : 0;
      const mobileInset = mobileChromeModeRef.current === "hidden" ? 18
        : mobileChromeModeRef.current === "compact" ? compactHeaderInset()
          : navHeight + headHeight + 18;
      const topInset = isMobile ? mobileInset : headHeight + 18;
      const viewportTop = isMobile ? 0 : el.getBoundingClientRect().top;
      adjustReaderScroll(target.getBoundingClientRect().top - viewportTop - topInset);
      return true;
    }
    return false;
  };

  const focusSourceAnchor = (ref, source) => {
    if (source && source.n) setOpenN(source.n);
    scrollToSegment(ref, source);
  };
  const returnToSourceAnchor = (ref, source) => {
    if (!isMobile) { focusSourceAnchor(ref, source); return; }
    closeMobileSheet();
    setMobileChrome("compact");
    requestAnimationFrame(() => requestAnimationFrame(() => focusSourceAnchor(ref, source)));
  };

  useEffectR(() => {
    const jumpPending = (ref, source) => {
      if (!ref) return;
      const token = ++pendingJumpTokenRef.current;
      clearTimeout(pendingJumpTimerRef.current);
      const land = (attempt = 0) => {
        if (token !== pendingJumpTokenRef.current) return;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (token !== pendingJumpTokenRef.current) return;
          if (scrollToSegment(ref, source)) return;
          if (attempt < 5) pendingJumpTimerRef.current = setTimeout(() => land(attempt + 1), 80);
        }));
      };
      pendingJumpTimerRef.current = setTimeout(() => land(), 40);
    };
    if (window.__mayanosPendingReaderStart) {
      delete window.__mayanosPendingReaderStart;
      setReaderScrollTop(0);
      requestAnimationFrame(() => setReaderScrollTop(0));
    }
    const pending = window.__mayanosPendingSegmentRef;
    if (pending) {
      delete window.__mayanosPendingSegmentRef;
      jumpPending(pending);
    }
    const onJump = (e) => jumpPending(e.detail && e.detail.ref, e.detail && e.detail.source);
    const onRestore = (e) => {
      const detail = (e && e.detail) || {};
      const restore = () => {
        const liveSources = Array.isArray(window.SOURCES) ? window.SOURCES : sources;
        const sourceRef = normalizeReaderRef(detail.sourceRef);
        const sourceAnchorRef = normalizeReaderRef(detail.sourceAnchorRef);
        const restoredSource = liveSources.find((source) => {
          if (sourceRef && normalizeReaderRef(source.srcRef || source.citation) !== sourceRef) return false;
          if (sourceAnchorRef) {
            const anchors = Array.isArray(source.anchorRefs) && source.anchorRefs.length ? source.anchorRefs : [source.anchorRef];
            if (!anchors.some((anchor) => normalizeReaderRef(anchor) === sourceAnchorRef)) return false;
          }
          return sourceRef || sourceAnchorRef;
        }) || liveSources.find((source) => detail.sourceN && Number(source.n) === Number(detail.sourceN));
        if (detail.segmentRef) scrollToSegment(detail.segmentRef, restoredSource || undefined);
        if (restoredSource) revealSourceInRail(restoredSource);
        if (scrollRef.current && Number.isFinite(Number(detail.scrollTop))) setReaderScrollTop(detail.scrollTop);
      };
      setTimeout(() => requestAnimationFrame(() => requestAnimationFrame(restore)), 80);
    };
    window.addEventListener("mayanos-scroll-segment", onJump);
    window.addEventListener("mayanos-restore-reader", onRestore);
    if (window.__mayanosPendingReaderRestore) {
      const detail = window.__mayanosPendingReaderRestore;
      delete window.__mayanosPendingReaderRestore;
      onRestore({ detail });
    }
    return () => {
      clearTimeout(pendingJumpTimerRef.current);
      window.removeEventListener("mayanos-scroll-segment", onJump);
      window.removeEventListener("mayanos-restore-reader", onRestore);
    };
  }, [sicha.id, sicha.segments.length, sources.length, isMobile]);

  const jumpSearchResult = (index) => {
    const count = searchResults.length;
    if (!count) return;
    const next = ((index % count) + count) % count;
    setSearchActiveIndex(next);
    const result = searchResults[next];
    if (result && result.ref) {
      const local = sicha.segments.some((segment) => segment.ref === result.ref || segment.id === result.ref);
      if (local) scrollToSegment(result.ref);
      else if (window.loadSicha) window.loadSicha(result.ref, { recordActivity: false });
    }
  };

  const sectionRefForBookmark = (ref) => {
    if (!ref) return ref;
    const match = tocSections
      .filter((s) => s && !s.isHeader && s.ref && (
        ref === s.targetRef || ref === s.ref || ref.indexOf(s.ref + ":") === 0
      ))
      .sort((a, b) => String(b.ref).length - String(a.ref).length)[0];
    if (match) return match.readerRef || match.ref;
    return String(ref).replace(/:\d+(?::\d+)*$/, "");
  };

  const openBookmark = (ref) => {
    if (!ref) return;
    if (ref === currentTextRef && scrollRef.current) {
      setReaderScrollTop(0);
      return;
    }
    if (sicha.segments.some((seg) => seg.ref === ref || seg.id === ref)) {
      scrollToSegment(ref);
      return;
    }
    const sectionRef = sectionRefForBookmark(ref);
    if (window.loadSicha) {
      window.loadSicha(sectionRef);
      setTimeout(() => scrollToSegment(ref), 900);
    }
  };

  const encodeReaderRef = (ref) => encodeURIComponent(String(ref || "").replace(/ /g, "_"));
  const localReaderUrl = (ref, segRef) => {
    const sectionRef = sectionRefForBookmark(ref || currentTextRef);
    const canonicalPath = window.__mayanosCanonicalReaderPathFor
      && window.__mayanosCanonicalReaderPathFor(sicha.collection, sectionRef || currentTextRef, segRef, toc);
    if (canonicalPath) return `${window.location.origin}${canonicalPath}`;
    // Unsupported legacy refs still get a working link, but never expose a
    // segment query string in the share UI.
    return `${window.location.origin}/reader/${encodeReaderRef(sectionRef || currentTextRef)}`;
  };
  const sectionShareRef = sectionRefForBookmark(currentTextRef);
  const requestedSegmentRef = (() => {
    try { return new URLSearchParams(window.location.search).get("seg") || ""; } catch (e) { return ""; }
  })();
  const shareSegment = activeSegment || sicha.segments.find((seg) => seg && (seg.ref === requestedSegmentRef || seg.id === requestedSegmentRef)) || sicha.segments[0] || null;
  const segmentShareRef = (shareSegment && (shareSegment.ref || shareSegment.id)) || activeSegmentRef || "";
  const segmentShareUrl = segmentShareRef ? localReaderUrl(sectionShareRef, segmentShareRef) : "";
  const copyShare = async (value, label, key) => {
    if (!value) return;
    if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("share_started", { surface: "reader", target: key || label || "link" });
    let copied = false;
    try {
      const nav = window.navigator || {};
      if (nav.clipboard && window.isSecureContext) {
        await nav.clipboard.writeText(value);
        copied = true;
      }
    } catch (e) {}
    if (!copied) {
      try {
        const t = document.createElement("textarea");
        t.value = value;
        t.setAttribute("readonly", "");
        t.style.position = "fixed";
        t.style.left = "-1000px";
        t.style.top = "0";
        document.body.appendChild(t);
        t.focus();
        t.select();
        copied = document.execCommand ? document.execCommand("copy") : false;
        document.body.removeChild(t);
      } catch (e) {}
    }
    if (!copied && key) {
      const field = document.querySelector(`[data-share-url="${key}"]`);
      if (field && field.select) {
        field.focus();
        field.select();
      }
    }
    setShareStatus(copied ? `${label} copied` : "Link selected");
    clearTimeout(window.__mayanosShareToast);
    window.__mayanosShareToast = setTimeout(() => setShareStatus(""), 1700);
  };
  const shareNative = async () => {
    const value = segmentShareUrl;
    if (!value) return;
    try {
      if (window.navigator && typeof window.navigator.share === "function") {
        await window.navigator.share({ title: sicha.titleEn || "Mayanos", text: `Read ${sicha.titleEn || "this text"} on Mayanos`, url: value });
        if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("share_started", { surface: "reader", target: "native" });
        setShareStatus("Share sheet opened");
        return;
      }
    } catch (error) { if (error && error.name === "AbortError") return; }
    copyShare(value, "Current segment link", "segment");
  };

  const tocIsCurrent = (section) => {
    const ref = currentTextRef || "";
    if (currentTitleSection && section && !section.isHeader) {
      const n = parseInt(String(section.ref || section.label || "").match(/(\d+)(?!.*\d)/)?.[1] || "", 10);
      if (Number.isFinite(n) && n === currentTitleSection.n) return true;
    }
    const target = section && (section.targetRef || section.ref);
    return !!(section && target && (ref === target || ref === section.ref || ref.indexOf(section.ref + ":") === 0));
  };

  const openTocSection = (section, closeOverlay) => {
    if (!section || !(section.targetRef || section.ref)) return;
    if (closeOverlay) setTocOpen(false);
    if (isMobile) setSheetOpen(false);
    window.loadSicha && window.loadSicha(section.targetRef || section.readerRef || section.ref, { focusSection: section.targetRef || section.readerRef || section.ref, alternateTexts: sicha.alternateTexts || [] });
  };

  const renderTocSectionRow = (entry, group, options = {}) => {
    const closeOverlay = options.closeOverlay === true;
    const searchRow = options.searchRow || null;
    const shortTitle = options.shortTitle || tocShortSectionTitle(entry, group && group.secondary);
    const onHere = tocIsCurrent(entry);
    const showHeTitle = !!entry.heTitle && tocNormalize(entry.heTitle) !== tocNormalize(shortTitle);
    return (
      <button
        key={`toc-row-${entry.ref}-${entry.tocIndex}`}
        className={"toc-section-row" + (onHere ? " on" : "") + (options.active ? " is-active" : "")}
        onClick={() => openTocSection(entry, closeOverlay)}
        onMouseEnter={options.searchIndex == null ? undefined : () => setTocSearchActiveIndex(options.searchIndex)}
        aria-current={onHere ? "location" : undefined}
      >
        <span className="toc-section-main">
          {searchRow && <span className="toc-result-path">
            {searchRow.primary.label} <span>›</span> {searchRow.group.secondary}
            {searchRow.group.heLabel && tocNormalize(searchRow.group.heLabel) !== tocNormalize(searchRow.group.secondary) && <em lang="he" dir="rtl" className="heb">{searchRow.group.heLabel}</em>}
          </span>}
          <strong>{shortTitle || entry.title}</strong>
          {entry.excerpt && <span className="toc-section-excerpt">{entry.excerpt}</span>}
        </span>
        {(showHeTitle || entry.heExcerpt) && (
          <span lang="he" dir="rtl" className="toc-section-he heb">
            {showHeTitle && <b>{entry.heTitle}</b>}
            {!entry.excerpt && entry.heExcerpt && <span>{entry.heExcerpt}</span>}
          </span>
        )}
        {onHere ? <span className="toc-here">Here</span> : <span className="toc-jump" aria-hidden="true">›</span>}
      </button>
    );
  };

  const renderTocGroups = (primary, closeOverlay) => primary ? (
    <div className="toc-section-groups">
      {primary.groups.map((group) => (
        <section className="toc-section-group" key={group.id}>
          <div className="toc-group-head">
            <div>
              <b>{group.secondary}</b>
              {group.heLabel && tocNormalize(group.heLabel) !== tocNormalize(group.secondary) && <span lang="he" dir="rtl" className="heb">{group.heLabel}</span>}
            </div>
            <span>{group.items.length} {group.items.length === 1 ? "section" : "sections"}</span>
          </div>
          <div className="toc-section-list">
            {group.items.map((entry) => renderTocSectionRow(entry, group, { closeOverlay }))}
          </div>
        </section>
      ))}
    </div>
  ) : <p className="toc-empty">No contents are available.</p>;

  const renderTocSearchResults = (closeOverlay) => tocSearchResults.length ? (
    <div className="toc-search-results" role="listbox" aria-label="Matching sections">
      {tocSearchResults.map((row, index) => renderTocSectionRow(row.entry, row.group, {
        closeOverlay,
        searchRow: row,
        shortTitle: row.shortTitle,
        searchIndex: index,
        active: index === tocSearchActiveIndex,
      }))}
    </div>
  ) : (
    <div className="toc-no-results">
      <Icon d="search" size={22} />
      <b>No section matches “{tocQ.trim()}”</b>
      <span>Try a title, maamar, sicha, letter, section number, Hebrew phrase, or opening words.</span>
    </div>
  );

  useEffectR(() => {
    setOpenGroups({});
    setClosedSourceNs([]);
  }, [activeSegmentRef]);

  useEffectR(() => {
    setOpenN((current) => current != null && !effectiveActiveNs.includes(current) ? null : current);
  }, [activeSegmentRef, effectiveActiveNs.join("|")]);

  useEffectR(() => {
    if (!activeNs.length) return;
    const next = {};
    sourceGroups.forEach(({ group, items }) => {
      if (group !== "Quoted Footnotes" && items.some((src) => activeNs.indexOf(src.n) >= 0)) next[group] = true;
    });
    if (Object.keys(next).length) setOpenGroups((prev) => Object.assign({}, prev, next));
  }, [activeNs.join("|"), sourceGroups]);

  // Margin mode belongs in the Resources rail. Keep the exact block's quoted
  // footnotes expanded there as the reader advances from paragraph to paragraph.
  useEffectR(() => {
    if (isMobile || fnStyle !== "margin") return;
    setTab("sources");
    setRailTool(null);
    setShowGraph(false);
    setRailCollapsed(false);
    setClosedSourceNs([]);
    setOpenGroups((previous) => Object.assign({}, previous, { "Quoted Footnotes": true }));
    setExpandedSourceGroups((previous) => Object.assign({}, previous, { "Quoted Footnotes": true }));
  }, [fnStyle, activeSegmentRef, isMobile]);

  const replaceReaderLocation = (sectionRef, segmentRef, scrollTop) => {
    if (!sectionRef) return;
    const record = (sicha._loadedSections || []).find((section) => (section.targetRef || section.ref) === sectionRef || section.ref === sectionRef);
    // A scroll callback from the outgoing text can run during source first
    // paint. It must never attach that old maamar's coordinate to the new book.
    if (!record && sicha._live && sectionRef !== sicha._liveTargetRef && sectionRef !== sicha._liveRef) return;
    const publicSectionRef = record && record.ref || sectionRef;
    const encodedSection = encodeReaderRef(publicSectionRef);
    const canonicalPath = window.__mayanosCanonicalReaderPathFor
      && window.__mayanosCanonicalReaderPathFor(sicha.collection, publicSectionRef, segmentRef, toc);
    const nextUrl = canonicalPath || `/reader/${segmentRef ? encodeReaderRef(segmentRef) : encodedSection}`;
    const state = history.state && typeof history.state === "object" ? history.state : {};
    if (location.pathname + location.search !== nextUrl) history.replaceState(state, "", nextUrl);
    window.__mayanosReaderLocation = {
      sectionRef,
      segmentRef: segmentRef || "",
      scrollTop: Math.max(0, Number(scrollTop) || 0),
      title: readerLocationTitle || sectionRef,
    };
  };

  const loadPreviousPreservingPosition = async () => {
    const el = scrollRef.current;
    if (!el || loadingPreviousRef.current || !window.loadPrevious || !sicha.prev) return false;
    const viewportTop = isMobile ? 12 : el.getBoundingClientRect().top + 12;
    const visible = Array.from(el.querySelectorAll(".seg")).find((node) => node.getBoundingClientRect().bottom > viewportTop);
    const anchorRef = visible && visible.getAttribute("data-seg-ref");
    const anchorTop = visible ? visible.getBoundingClientRect().top : 0;
    loadingPreviousRef.current = true;
    const loaded = await window.loadPrevious();
    if (loaded) {
      setTimeout(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        const same = anchorRef && Array.from(el.querySelectorAll(".seg")).find((node) => node.getAttribute("data-seg-ref") === anchorRef);
        if (same) adjustReaderScroll(same.getBoundingClientRect().top - anchorTop);
        loadingPreviousRef.current = false;
      })), 0);
    } else loadingPreviousRef.current = false;
    return !!loaded;
  };

  const jumpSection = (ref) => {
    if (!ref || !window.loadSicha) return;
    setShareOpen(false);
    setSettingsOpen(false);
    setTocOpen(false);
    setDictionaryPeek(null);
    if (isMobile) {
      setSheetOpen(false);
      showFullMobileChrome();
    }
    const loadedSection = (sicha._loadedSections || []).find((section) =>
      [section.ref, section.targetRef, section.canonicalRef].includes(ref));
    const loadedSegment = loadedSection && sicha.segments.find((segment) =>
      [loadedSection.ref, loadedSection.targetRef, loadedSection.canonicalRef].filter(Boolean).includes(segment._sectionRef)
      || segment.ref === ref || segment.ref?.startsWith(ref + ":"));
    if (loadedSegment && scrollToSegment(loadedSegment.ref)) return;
    Promise.resolve(window.loadSicha(ref, { focus: false, includeFocus: false }))
      .then((loaded) => { if (loaded) setReaderScrollTop(0); });
  };

  const returnToOrigin = () => {
    if (!returnOrigin) return;
    if (history.state && history.state.mayanosOrigin) {
      history.back();
      return;
    }
    if (window.loadSicha && returnOrigin.ref) {
      window.loadSicha(returnOrigin.ref, { historyMode: "replace", origin: null, restore: returnOrigin }).then((loaded) => {
        if (!loaded) return;
        window.dispatchEvent(new CustomEvent("mayanos-restore-reader", { detail: returnOrigin }));
      });
    }
  };

  // Progress, active-section URL, and bidirectional continuous loading.
  useEffectR(() => {
    const el = scrollRef.current; if (!el) return;
    const scrollTarget = isMobile ? window : el;
    lastSegRef.current = -1;
    lastScrollTopRef.current = readerScrollTop(el);
    const on = (event) => {
      const previousTop = lastScrollTopRef.current;
      const currentTop = readerScrollTop(el);
      if (isMobile && currentTop > 0) lastDocumentScrollTopRef.current = currentTop;
      const delta = currentTop - previousTop;
      const movingUp = delta < 0;
      lastScrollTopRef.current = currentTop;
      const viewportHeight = readerViewportHeight(el);
      const scrollHeight = readerScrollHeight(el);
      const readingProgress = currentTop / Math.max(1, scrollHeight - viewportHeight);
      const manuallySyncing = Date.now() < manualSyncUntilRef.current;
      setProgress(readingProgress);

      const userDrivenScroll = Date.now() - lastReaderInputRef.current < 900;
      if (isMobile && !sheetOpen && !settingsOpen && !shareOpen) {
        const direction = delta > 0 ? 1 : delta < 0 ? -1 : 0;
        if (currentTop <= 36) {
          chromeDirectionRef.current = 0;
          chromeTravelRef.current = 0;
          setMobileChrome("full");
        } else if (userDrivenScroll && direction && Math.abs(delta) >= 1) {
          if (chromeDirectionRef.current !== direction) {
            chromeDirectionRef.current = direction;
            chromeTravelRef.current = 0;
          }
          chromeTravelRef.current += Math.abs(delta);
          // Use section-sized hysteresis: the top rail should return only
          // after a real upward reading gesture, not a tiny finger reversal.
          // Require roughly one full viewport of upward reading before the
          // top rail returns. The distance resets whenever the gesture
          // changes direction, so a tiny reversal cannot expand the rail.
          const chromeRevealDistance = Math.max(720, Math.min(1440, viewportHeight * 1.25));
          if (direction < 0 && chromeTravelRef.current >= chromeRevealDistance) {
            setMobileChrome("full");
            chromeTravelRef.current = 0;
          } else if (direction > 0 && chromeTravelRef.current >= 64) {
            setMobileChrome("compact");
            chromeTravelRef.current = 0;
          }
        }
      } else if (!isMobile && mobileChromeModeRef.current !== "full") {
        // Desktop keeps the full reader chrome available. Do not run the
        // mobile hide/show state machine here; that was the source of desktop
        // state churn even though the desktop CSS kept the bar visible.
        chromeDirectionRef.current = 0;
        chromeTravelRef.current = 0;
        setMobileChrome("full");
      }

      clearTimeout(activityTimerRef.current);
      activityTimerRef.current = setTimeout(() => {
        if (!window.MAYANOS_ACTIVITY || !sicha._liveRef) return;
        const active = sicha.segments[lastSegRef.current];
        window.MAYANOS_ACTIVITY.update(sicha._liveRef, readingProgress, active && active.ref);
        window.CONTINUE = window.MAYANOS_ACTIVITY.cards();
      }, 700);
      const forwardPreloadDistance = Math.max(2400, viewportHeight * 2.4);
      const backwardPreloadDistance = Math.max(900, viewportHeight * 1.2);

      // Opening a specific sicha/maamar establishes the top of the reading
      // surface. Do not silently prepend earlier sections when the reader
      // scrolls upward; that made the selected text jump back into the sefer.
      // Previous sections remain available through the explicit Previous
      // control (and are still used when preparing a full print view).
      const backwardPagingEnabled = false;
      if (backwardPagingEnabled && !manuallySyncing && event && movingUp && userDrivenScroll && currentTop < backwardPreloadDistance) loadPreviousPreservingPosition();
      if (manuallySyncing && event) return;
      // scroll-sync: find the block at the top of the viewport, surface ITS footnotes
      const segEls = el.querySelectorAll(".seg");
      const navInset = isMobile ? Math.ceil(document.querySelector(".topnav")?.getBoundingClientRect().height || 0) : 0;
      const fullHeadInset = navInset + (readHeadRef.current ? readHeadRef.current.getBoundingClientRect().height : 0) + 18;
      const visibleTop = isMobile
        ? (mobileChromeModeRef.current === "compact" ? compactHeaderInset() : mobileChromeModeRef.current === "hidden" ? 18 : fullHeadInset)
        : el.getBoundingClientRect().top + fullHeadInset;
      const visibleBottom = isMobile ? window.innerHeight : el.getBoundingClientRect().bottom;
      // Track the reader's visual focus rather than the very top edge. A newly
      // entered section becomes current while it is moving into the reading
      // zone, so the title and source rail transition before the old section
      // has nearly disappeared.
      const readingLine = visibleTop + Math.max(72, (visibleBottom - visibleTop) * 0.42);
      let idx = -1;
      for (let i = 0; i < segEls.length; i++) {
        if (segEls[i].getBoundingClientRect().bottom > readingLine) { idx = i; break; }
      }
      if (idx >= 0 && idx !== lastSegRef.current) {
        lastSegRef.current = idx;
        const d = sicha.segments[idx];
        setActiveSegId(d ? (d.ref || d.id) : null);
        const sectionRef = d && (d._sectionRef || sicha._liveRef || sicha.id);
        if (sectionRef) {
          setActiveSectionRef(sectionRef);
          replaceReaderLocation(sectionRef, d.ref || d.id, currentTop);
        }
        const markerNs = d ? ((String(d.he) + " " + String(d.en)).match(/‹(\d+)›/g) || []).map((x) => parseInt(x.replace(/[‹›]/g, ""), 10)) : [];
        const relatedNs = d ? sources.filter((s) => sourceMatchesSegment(s, d)).map((s) => s.n) : [];
        const ns = applyActiveSources(markerNs.concat(relatedNs));
        if (ns.length && railRef.current && tab === "sources" && !showGraph) {
          // scroll whichever rail container actually scrolls so the active
          // mareh makom comes into view (robust to layout)
          let cont = railRef.current.querySelector(".rail-body");
          if (!cont || cont.scrollHeight <= cont.clientHeight + 4) cont = railRef.current;
          const node = cont.querySelector(`[data-src-n="${ns[0]}"]`);
          if (node) {
            const nr = node.getBoundingClientRect(), cr = cont.getBoundingClientRect();
            cont.scrollTop += (nr.top - cr.top) - 18;
          }
        }
      }
    };
    scrollTarget.addEventListener("scroll", on, { passive: true });
    on();
    return () => {
      scrollTarget.removeEventListener("scroll", on);
      clearTimeout(activityTimerRef.current);
      if (window.MAYANOS_ACTIVITY && sicha._liveRef) {
        const finalProgress = readerScrollTop(el) / Math.max(1, readerScrollHeight(el) - readerViewportHeight(el));
        const active = sicha.segments[lastSegRef.current];
        window.MAYANOS_ACTIVITY.update(sicha._liveRef, finalProgress, active && active.ref);
        window.CONTINUE = window.MAYANOS_ACTIVITY.cards();
      }
    };
  }, [effectiveLang, sicha.id, sicha.segments.length, sources.length, tab, showGraph, readerLocationTitle, isMobile, sheetOpen, settingsOpen, shareOpen]);

  // mobile: horizontal swipe across the text switches language (עב ⇄ both ⇄ EN)
  useEffectR(() => {
    if (!isMobile) return;
    const el = scrollRef.current; if (!el) return;
    const order = [hasHebrewText ? "he" : null, hasHebrewText && hasEnglishText ? "both" : null, hasEnglishText ? "en" : null].filter(Boolean);
    let sx = 0, sy = 0, t0 = 0, ok = false;
    const onStart = (e) => {
      if (e.touches.length !== 1) { ok = false; return; }
      const t = e.touches[0]; sx = t.clientX; sy = t.clientY; t0 = Date.now(); ok = true;
    };
    const onEnd = (e) => {
      if (!ok) return; ok = false;
      if (window.getSelection && String(window.getSelection())) return; // don't fight a selection
      const t = e.changedTouches[0]; const dx = t.clientX - sx, dy = t.clientY - sy;
      if (Date.now() - t0 > 500 || sheetOpen || fnPop) return;
      if (Math.abs(dx) < 96 || Math.abs(dx) < Math.abs(dy) * 2.25) return;
      const i = order.indexOf(effectiveLang);
      const ni = dx < 0 ? Math.min(order.length - 1, i + 1) : Math.max(0, i - 1);
      if (ni === i || !onLang) return;
      onLang(order[ni]);
      setLangToast({ he: "עברית", both: "Hebrew + English", en: "English" }[order[ni]]);
      clearTimeout(window.__mlt); window.__mlt = setTimeout(() => setLangToast(null), 1100);
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchend", onEnd, { passive: true });
    return () => { el.removeEventListener("touchstart", onStart); el.removeEventListener("touchend", onEnd); };
  }, [isMobile, effectiveLang, hasHebrewText, hasEnglishText, onLang, sheetOpen, fnPop]);

  // one-time gesture hint
  useEffectR(() => {
    if (!isMobile) return;
    try { if (localStorage.getItem("mayanos-gesture-hint")) return; } catch (e) {}
    setSwipeHint(true);
    const id = setTimeout(() => { setSwipeHint(false); try { localStorage.setItem("mayanos-gesture-hint", "1"); } catch (e) {} }, 4600);
    return () => clearTimeout(id);
  }, [isMobile]);

  const openSource = (n) => {
    const src = sources.find((s) => s.n === n);
    if (src) {
      setFocusedMarkerN(Number(src.originFootnoteN || (src.footnote ? src.n : 0)) || null);
      revealSourceInRail(src);
    }
  };
  // Unified footnote-marker tap. In desktop inline mode, the marker reveals the
  // blue citation text and that citation text itself opens the source rail.
  const handleMarker = (n, e) => {
    setFocusedMarkerN(n);
    if (!isMobile && fnStyle === "inline") {
      if (e?.currentTarget?.classList?.contains("fninline-text")) {
        openSource(n);
      } else {
        setFnInline((prev) => prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]);
      }
      return;
    }
    if (!isMobile) {
      openSource(n);
      return;
    }
    if (effFnMode === "popover") {
      const r = e.currentTarget.getBoundingClientRect();
      const popW = Math.min(window.innerWidth * 0.86, 340);
      const half = popW / 2 + 8;
      const flip = r.bottom + 8 > window.innerHeight - 230;
      const top = flip ? r.top - 8 : r.bottom + 8;
      setFnPop({ n, pos: { top, left: Math.min(Math.max(r.left + r.width / 2, half), window.innerWidth - half) }, flip });
    } else if (effFnMode === "inline") {
      setFnInline((prev) => prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]);
    } else {
      openSource(n); // sheet
    }
  };
  // dismiss the footnote popover on scroll
  useEffectR(() => {
    if (!fnPop) return;
    const el = scrollRef.current;
    const scrollTarget = isMobile ? window : el;
    const close = () => setFnPop(null);
    if (scrollTarget) scrollTarget.addEventListener("scroll", close, { passive: true });
    return () => { if (scrollTarget) scrollTarget.removeEventListener("scroll", close); };
  }, [fnPop, isMobile]);
  const popSrc = fnPop ? sources.find((s) => s.n === fnPop.n) : null;

  // Language is user-controlled in EVERY layout. In the side-by-side views,
  // picking a single language collapses the pair down to one clean column.
  const showHe = effectiveLang !== "en";
  const showEn = effectiveLang !== "he";
  const bothLangs = showHe && showEn;
  const sideBySide = (effLayout === "split" || effLayout === "columns") && bothLangs;
  const inlineFootnotes = isMobile ? effFnMode === "inline" : fnStyle === "inline";
  const mobileViewportHeight = () => Math.round((typeof window !== "undefined" && window.visualViewport && window.visualViewport.height) || (typeof window !== "undefined" && window.innerHeight) || 760);
  const sheetMaxHeight = () => Math.max(SHEET_MIN, Math.round(mobileViewportHeight() * 0.88));
  const openSheet = () => {
    setTab("sources");
    setRailTool(null);
    setShowGraph(false);
    setRailCollapsed(false);
    openMobileSheet();
    setTimeout(() => setUsefulSheetHeight(0.4), 0);
  };
  const segSources = (seg) => sources.filter((s) => s.footnote && (
    seg.he.includes(`‹${s.n}›`) || seg.en.includes(`‹${s.n}›`) || sourceMatchesSegment(s, seg)
  ));
  const inlineFootnoteNs = useMemoR(() => uniqNumbers(sources.filter((source) => source.footnote).map((source) => source.n)), [sources]);
  const openInlineFootnotes = () => {
    setInlineOpenAll(true);
    setFnInline(inlineFootnoteNs);
  };
  const closeInlineFootnotes = () => {
    setInlineOpenAll(false);
    setFnInline([]);
  };
  useEffectR(() => {
    if (inlineOpenAll) setFnInline(inlineFootnoteNs);
  }, [inlineOpenAll, inlineFootnoteNs.join("|")]);

  // resizable sheet: drag the grip to set height; release very low → close.
  const SHEET_MIN = 140;
  const defaultSheetH = () => Math.round(mobileViewportHeight() * 0.4);
  const curSheetH = Math.min(sheetMaxHeight(), Math.max(SHEET_MIN, sheetH || defaultSheetH()));
  const setUsefulSheetHeight = (ratio) => {
    const nextRatio = Math.max(0.18, Math.min(0.88, Number(ratio) || 0.4));
    const next = Math.min(sheetMaxHeight(), Math.max(SHEET_MIN, Math.round(mobileViewportHeight() * nextRatio)));
    setSheetH(next);
    try { localStorage.setItem("mayanos-sheet-ratio", String(next / mobileViewportHeight())); } catch (err) {}
  };
  const toggleSheetHeight = () => {
    const expanded = curSheetH > mobileViewportHeight() * 0.68;
    setUsefulSheetHeight(expanded ? 0.4 : 0.84);
  };
  const startSheetDrag = (e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    if (sheetDragCleanupRef.current) sheetDragCleanupRef.current();
    const vh = mobileViewportHeight();
    const max = Math.max(SHEET_MIN, Math.round(vh * 0.88));
    let lastY = Number(e.clientY);
    if (!Number.isFinite(lastY)) return;
    setDragging(true);
    const move = (ev) => {
      if (Number.isFinite(Number(ev.clientY))) lastY = Number(ev.clientY);
      const h = Math.min(max, vh - lastY);
      setSheetH(Math.max(60, h));
    };
    const cleanup = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", end);
      document.removeEventListener("pointercancel", end);
      sheetDragCleanupRef.current = null;
    };
    const end = (ev) => {
      if (Number.isFinite(Number(ev.clientY))) lastY = Number(ev.clientY);
      cleanup();
      setDragging(false);
      const h = Math.min(max, vh - lastY);
      if (h < SHEET_MIN - 30) { closeMobileSheet(); }
      else {
        const clamped = Math.max(SHEET_MIN, h);
        setSheetH(clamped);
        try { localStorage.setItem("mayanos-sheet-ratio", String(clamped / vh)); } catch (err) {}
      }
    };
    sheetDragCleanupRef.current = cleanup;
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", end);
    document.addEventListener("pointercancel", end);
  };

  useEffectR(() => () => {
    if (sheetDragCleanupRef.current) sheetDragCleanupRef.current();
  }, []);

  useEffectR(() => {
    if (!isMobile) return;
    const viewport = window.visualViewport;
    const onResize = () => {
      if (!sheetH) return;
      const max = sheetMaxHeight();
      const ratio = Number(localStorage.getItem("mayanos-sheet-ratio"));
      const next = Number.isFinite(ratio) && ratio > 0
        ? Math.round(mobileViewportHeight() * ratio)
        : sheetH;
      const clamped = Math.min(max, Math.max(SHEET_MIN, next));
      if (clamped !== sheetH) setSheetH(clamped);
    };
    window.addEventListener("orientationchange", onResize);
    if (viewport) viewport.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("orientationchange", onResize);
      if (viewport) viewport.removeEventListener("resize", onResize);
    };
  }, [isMobile, sheetH]);

  useEffectR(() => {
    if (!collectionNotice) return undefined;
    const timer = setTimeout(() => setCollectionNotice(null), 5200);
    return () => clearTimeout(timer);
  }, [collectionNotice]);

  const renderSourceGroup = ({ group, items }) => {
    const isOpen = Object.prototype.hasOwnProperty.call(openGroups, group)
      ? openGroups[group]
      : group !== "Quoted Footnotes";
    const expanded = !!expandedSourceGroups[group];
    const shownItems = expanded ? items : items.slice(0, 50);
    return (
      <div className="rail-resource-group" key={group}>
        <button className={"rail-resource-head" + (isOpen ? " on" : "")} onClick={() => setOpenGroups((prev) => Object.assign({}, prev, { [group]: !isOpen }))}>
          <span className="rail-resource-book"><Icon d="book" size={18} /></span>
          <span>{group}</span>
          <SourceLangBadges sources={items} compact />
          <em>{items.length}</em>
          <Icon d={isOpen ? "chevd" : "chev"} size={14} />
        </button>
        {isOpen && (
          <div className="rail-resource-list">
            {shownItems.map((s, i) => (
              <React.Fragment key={s.id}>
                {i === 0 && <div className="resource-subhead">For this block</div>}
                <div data-src-n={s.n} className={"srcwrap" + (hoverN === s.n ? " hi" : "") + (activeSet.has(s.n) ? " active" : "") + (openN === s.n ? " selected" : "")}>
                  <SourceCard src={s} expanded={openSourceSet.has(s.n)}
                    onToggle={() => toggleSourceCard(s.n)} onAdd={addWs} onSaveFolder={setFolderSource} onFocusAnchor={returnToSourceAnchor} anchorSegment={activeSegment} collected={isCollected(s)} />
                </div>
              </React.Fragment>
            ))}
            {items.length > 50 && <button className="mini-btn" style={{ margin: 8 }} onClick={() => setExpandedSourceGroups((prior) => Object.assign({}, prior, { [group]: !expanded }))}>{expanded ? "Show fewer" : `Open all ${items.length}`}</button>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={"reader" + (isLikkuteiSichos ? " reader-likkutei-sichos" : "") + (railCollapsed && !isMobile ? " rail-collapsed" : "") + (settingsOpen ? " settings-open" : "") + (shareOpen ? " share-open" : "")}>
      {/* progress */}
      <div className="read-progress"><span style={{ width: `${progress * 100}%` }} /></div>

      {/* On mobile the exact book and section never disappear while reading. */}
      <nav ref={compactBarRef} className={"reader-compactbar" + (isMobile && (sheetOpen || mobileChromeMode !== "full") ? " on" : "")}
        aria-label="Current reading location"
        aria-hidden={!(isMobile && (sheetOpen || mobileChromeMode !== "full"))}
        inert={isMobile && (sheetOpen || mobileChromeMode !== "full") ? undefined : true}>
        <button type="button" className="reader-compact-icon" onClick={onBack} aria-label="Back to Library">
          <Icon d="back" size={18} />
        </button>
        <button type="button" className="reader-compact-location"
          onClick={() => toc && toc.sections && toc.sections.length > 1 && setTocOpen(true)}
          title={`${readerCollectionLabel} · ${readerLocationTitle} · ${readerLocationHe || ""}`}
          aria-label={`Current location: ${readerLocationTitle}`}>
          <small>{readerCollectionLabel}</small>
          <span>
            <b>{readerLocationTitle}</b>
            {readerLocationHe && <em lang="he" dir="rtl" className="heb">{readerLocationHe}</em>}
          </span>
        </button>
        <div className="reader-compact-actions" aria-label="Reader navigation">
          <button type="button" className="reader-compact-action" disabled={!activeSectionInfo.prev} onClick={() => jumpSection(activeSectionInfo.prev)} aria-label="Previous section" title="Previous section"><Icon d="back" size={17} /></button>
          <button type="button" className="reader-compact-action" disabled={!activeSectionInfo.next} onClick={() => jumpSection(activeSectionInfo.next)} aria-label="Next section" title="Next section"><Icon d="back" size={17} style={{ transform: "scaleX(-1)" }} /></button>
          <button type="button" className="reader-compact-action" onClick={toggleReaderSettings} aria-label="Text settings" title="Text settings"><span className="reader-a">A</span></button>
          <button type="button" className="reader-compact-action reader-print-btn" onClick={prepareTextForPrint} disabled={printPreparing || sicha._gathering} aria-label={printPreparing ? "Preparing text for print" : "Print text"} title="Print text and settings" aria-busy={printPreparing || undefined}><Icon d="printer" size={17} /></button>
        </div>
        <button type="button" className="reader-compact-source" onClick={sheetOpen ? closeMobileSheet : openSheet}
          aria-label={sheetOpen ? "Close sources" : `Open sources panel, ${activeNs.length || sources.length} sources for the current passage`}>
          <Icon d={sheetOpen ? "close" : "graph"} size={17} />
          <span>{activeNs.length || sources.length}</span>
        </button>
        <div className="reader-compact-details">
          {readerCollectionHeLabel && <span lang="he" dir="rtl" className="heb">{readerCollectionHeLabel}</span>}
          {window.MAYANOS_SPONSORSHIP?.compact && <span lang="he" dir="rtl" className="heb reader-compact-sponsor">{window.MAYANOS_SPONSORSHIP.compact}</span>}
        </div>
        {returnOrigin && (
          <button type="button" className="reader-return reader-compact-return" data-testid="reader-compact-return" onClick={returnToOrigin} title={`Return to ${returnLabel}`}>
            <Icon d="back" size={15} />
            <span>Back to {returnLabel}</span>
          </button>
        )}
      </nav>

      {/* main scroll */}
      <div className="read-scroll" ref={scrollRef}
        style={isMobile && sheetOpen ? { paddingBottom: curSheetH + 32 } : undefined}>
        {sourceNotice && <div className="reader-source-notice" role="status"><span>{sourceNotice}</span><button className="mini-btn" aria-label="Dismiss source message" onClick={() => setSourceNotice("")}><Icon d="close" size={14} /></button></div>}
        <div className="read-head" ref={readHeadRef}
          aria-hidden={isMobile && (sheetOpen || mobileChromeMode !== "full")}
          inert={isMobile && (sheetOpen || mobileChromeMode !== "full") ? true : undefined}>
          <button className="ghost-btn" onClick={onBack}><Icon d="back" size={16} /> Library</button>
          {returnOrigin && (
            <button className="reader-return" data-testid="reader-return" onClick={returnToOrigin} title={`Return to ${returnLabel}`}>
              <Icon d="back" size={15} />
              <span>Back to {returnLabel}</span>
            </button>
          )}
          <button
            className="read-location"
            onClick={() => toc && toc.sections && toc.sections.length > 1 && setTocOpen(true)}
            title={readerLocationTitle}
            aria-label={`Current location: ${readerLocationTitle}`}
          >
            <span className="read-location-kicker">{readerCollectionLabel}</span>
            <span className="read-location-title">
              <Icon d="book" size={14} />
              <span>{readerLocationTitle}</span>
              {readerLocationHe && <em lang="he" dir="rtl" className="heb">{readerLocationHe}</em>}
              {readerCollectionHeLabel && <em lang="he" dir="rtl" className="heb read-location-collection-he">{readerCollectionHeLabel}</em>}
              {toc && toc.sections && toc.sections.length > 1 && <Icon d="chevd" size={13} style={{ opacity: .55 }} />}
            </span>
          </button>
          <div className="read-head-actions">
            {toc && toc.sections && toc.sections.length > 1 && (
              <button className="reader-icon-btn reader-contents-btn" onClick={() => { setTocOpen(true); setShareOpen(false); setSettingsOpen(false); setMoreOpen(false); }} title="Open sefer contents" aria-label="Open sefer contents"><Icon d="contents" size={20} /></button>
            )}
            <button className="reader-icon-btn" data-testid="reader-previous" disabled={!activeSectionInfo.prev} onClick={() => jumpSection(activeSectionInfo.prev)} title="Previous section" aria-label="Previous section"><Icon d="back" size={16} /></button>
            <button className="reader-icon-btn" data-testid="reader-next" disabled={!activeSectionInfo.next} onClick={() => jumpSection(activeSectionInfo.next)} title="Next section" aria-label="Next section"><Icon d="back" size={16} style={{ transform: "scaleX(-1)" }} /></button>
            <button className={"reader-icon-btn" + (settingsOpen ? " on" : "")} onClick={() => { toggleReaderSettings(); setMoreOpen(false); }} title="Text settings" aria-label="Text settings">
              <span className="reader-a">A</span>
            </button>
            <button type="button" className="reader-print-btn" onClick={prepareTextForPrint} disabled={printPreparing || sicha._gathering} aria-label={printPreparing ? "Preparing text for print" : "Print text"} aria-busy={printPreparing || undefined} title="Print text and settings"><Icon d="printer" size={17} /></button>
            <button className={"reader-icon-btn reader-more-btn" + (moreOpen ? " on" : "")} onClick={() => (moreOpen ? closeMore() : openMore())}
              aria-label="More actions" aria-haspopup="menu" aria-expanded={moreOpen}>
              <Icon d="dots" size={18} fill="currentColor" />
            </button>
          </div>
          {(moreOpen || moreClosing) && (
            <div className={"read-more-menu" + (moreClosing ? " closing" : "")} role="menu" aria-label="More reader actions" ref={moreMenuRef}>
                <button role="menuitem" className={bookmarked ? "on" : ""} onClick={() => { toggleBookmark(); closeMore(); }}>
                  <Icon d="bookmark" size={15} fill={bookmarked ? "currentColor" : undefined} /> {bookmarked ? "Bookmark saved" : "Bookmark this block"}
                </button>
                <button role="menuitem" onClick={() => { setShareOpen(true); setSettingsOpen(false); closeMore(); }}>
                  <Icon d="link" size={15} /> Share or copy link
                </button>
                <button role="menuitem" onClick={() => { openRail("sources", "search"); closeMore(); }}>
                  <Icon d="search" size={15} /> Find in this sefer
                </button>
                <button role="menuitem" disabled={printPreparing || sicha._gathering} onClick={() => { prepareTextForPrint(); closeMore(); }} aria-busy={printPreparing || undefined}>
                  <Icon d="printer" size={15} /> {printPreparing ? "Preparing text…" : "Print text"}
                </button>
            </div>
          )}
          {window.MAYANOS_SPONSORSHIP?.compact && <div className="reader-sponsor" role="note"><span>{window.MAYANOS_SPONSORSHIP.label}</span> <b lang="he" dir="rtl" className="heb">{window.MAYANOS_SPONSORSHIP.compact}</b></div>}
        </div>

        <div className={"read-share" + (shareOpen ? " open" : "")}>
          <div className="read-share-head">
            <span><Icon d="link" size={15} /> Share this text</span>
            <span className="read-share-head-actions">
              {shareStatus && <em>{shareStatus}</em>}
              <button className="mini-btn share-action-primary" onClick={shareNative} aria-label="Share current segment" title="Share current segment"><Icon d="link" size={15} /></button>
              <button className="reader-share-close" onClick={() => setShareOpen(false)} aria-label="Close share panel" title="Close share panel"><Icon d="close" size={15} /></button>
            </span>
          </div>
          <div className="read-share-grid">
            <div className="read-share-row">
              <span>
                <b>Current segment</b>
                <i>{segmentShareRef || "Current reading position"}</i>
                <input data-share-url="segment" value={segmentShareUrl || ""} readOnly aria-label="Current segment link" />
              </span>
              <button className="mini-btn share-copy-btn" disabled={!segmentShareUrl} onClick={() => copyShare(segmentShareUrl, "Current segment link", "segment")}><Icon d="copy" size={13} /> Copy link</button>
            </div>
          </div>
        </div>

        {/* reading controls (layout · language · text size) — in the reader now,
            not the global nav (cleaner). Shown on desktop and mobile. */}
        <div className={"read-toolbar" + (settingsOpen ? " open" : "")}>
          <div className="read-settings-head">
            <span><span className="reader-a">A</span> Reader settings</span>
            <div className="read-settings-head-actions">
              <button className="ghost-btn" onClick={() => onResetDisplay && onResetDisplay()}><Icon d="sliders" size={13} /> Reset</button>
              <button className="reader-settings-close" onClick={() => setSettingsOpen(false)} aria-label="Close reader settings" title="Close reader settings"><Icon d="close" size={15} /></button>
            </div>
          </div>
          <div className="read-groups">
          <section className="read-group" data-settings-group="reading">
            <h3 className="read-group-title">Reading</h3>
          <div className="read-setting" data-setting="language">
            <span className="read-setting-label">Language</span>
            <div className="dirswitch" style={{ padding: 2 }}>
              {[["en", "English"], ["both", "Bilingual"], ["he", "עברית"]].map(([k, l]) => (
                <button key={k} className={"dir-btn" + (effectiveLang === k ? " on" : "")} style={{ padding: "6px 11px" }}
                  disabled={(k === "he" && !hasHebrewText) || (k === "en" && !hasEnglishText) || (k === "both" && (!hasHebrewText || !hasEnglishText))}
                  title={!hasEnglishText && k !== "he" ? "No English translation for this sefer yet" : undefined}
                  onClick={() => onLang && onLang(k)}>{l}</button>
              ))}
            </div>
          </div>
          <div className="read-setting" data-setting="layout">
            <span className="read-setting-label">Layout</span>
            <LayoutPicker value={effLayout} onChange={(v) => onLayout && onLayout(v)} />
          </div>
          <div className="read-setting read-setting-size" data-setting="size">
            <span className="read-setting-label">Size</span>
            <button className="reader-step-btn" onClick={() => onScale && onScale(Math.max(8, readScale - 1))} aria-label="Smaller text"><span style={{ fontSize: 13, fontWeight: 850 }}>A</span></button>
            <input type="range" min="8" max="26" step="1" value={readScale} onChange={(e) => onScale && onScale(parseInt(e.target.value, 10))} aria-label="Text size" />
            <button className="reader-step-btn" onClick={() => onScale && onScale(Math.min(26, readScale + 1))} aria-label="Larger text"><span style={{ fontSize: 19, fontWeight: 850 }}>A</span></button>
            <em>{readScale}px</em>
          </div>
          <div className="read-setting" data-setting="font">
            <label className="read-setting-label" htmlFor="reader-font">Font</label>
            <div className="read-font-control">
              <select id="reader-font" value={readerFontValue(readFont)} onChange={(e) => onFont && onFont(e.target.value)}>
                {READER_FONTS.map(([value, label, note]) => (
                  <option key={value} value={value}>{label}{note ? ` · ${note}` : ""}</option>
                ))}
              </select>
              <span className="read-font-sample heb" dir="rtl" lang="he" data-reading-font={readerFontValue(readFont)} aria-hidden="true">אברהם אבינו</span>
            </div>
          </div>
          </section>
          <section className="read-group" data-settings-group="notes">
            <h3 className="read-group-title">Notes</h3>
          <div className="read-setting" data-setting="footnotes">
            <span className="read-setting-label">Footnotes</span>
            <div className={"reader-segments" + (!isMobile ? " footnote-mode-toggle" : "")}>
              {(isMobile ? [["sheet", "Sheet"], ["popover", "Pop-up"], ["inline", "Inline"]] : [["inline", "Inline"], ["margin", "Sidebar"]]).map(([k, l]) => (
                <button key={k} className={(isMobile ? fnMode : fnStyle) === k ? "on" : ""} onClick={() => {
                  if (isMobile) {
                    if (onFnMode) onFnMode(k);
                    if (k === "inline") {
                      setInlineOpenAll(false);
                      setFnInline([]);
                    }
                  } else {
                    if (onFnStyle) onFnStyle(k);
                    if (k === "margin") openAllFootnotes();
                    if (k === "inline") {
                      setInlineOpenAll(false);
                      setFnInline([]);
                    }
                  }
                }}>{l}</button>
              ))}
            </div>
          </div>
          {inlineFootnotes && (
            <div className="read-setting read-setting-inline" data-setting="inline-notes">
              <span className="read-setting-label">Open notes</span>
              <div className="dirswitch" style={{ padding: 2 }}>
                <button type="button" className={"dir-btn" + (!inlineOpenAll ? " on" : "")} style={{ padding: "6px 11px" }} onClick={closeInlineFootnotes}>Keep closed</button>
                <button type="button" className={"dir-btn" + (inlineOpenAll ? " on" : "")} style={{ padding: "6px 11px" }} onClick={openInlineFootnotes}>Open all</button>
              </div>
              <em className="read-setting-note">Notes for the passage you are reading open under the text.</em>
            </div>
          )}
          {!isMobile && fnStyle === "margin" && (
            <em className="read-setting-note">Quoted footnotes appear in Resources for this block.</em>
          )}
          </section>
          <section className="read-group" data-settings-group="display">
            <h3 className="read-group-title">Display</h3>
          <div className="read-setting" data-setting="display">
            <span className="read-setting-label">Mode</span>
            {window.ThemeToggle
              ? <window.ThemeToggle direction={direction} onDirection={onDirection} caption />
              : null}
          </div>
          </section>
          </div>
          <div className="read-settings-foot">
            <button type="button" className="ghost-btn reader-shortcuts-link" onClick={() => window.openShortcuts && window.openShortcuts()}>Keyboard shortcuts</button>
          </div>
        </div>

        <header className="reader-intro-modern">
          <div className="reader-intro-context">{readerCollectionLabel || sicha.collection}</div>
          <h1 className="heb" lang="he" dir="rtl">{sicha.titleHe || readerLocationHe || readerLocationTitle}</h1>
          <p className="reader-intro-reference">{readerLocationTitle}</p>
          <div className="reader-quick-controls">
            <button className="reader-quick-sources" onClick={() => openRail("sources")}><Icon d="layers" size={15} /> Sources <span>{activeNs.length || sources.length}</span></button>
            {gemaraDaf && (
              <span className="reader-quick-chip" title="Talmud page">
                <b lang="he" dir="rtl" className="heb">דף {heNumeral(gemaraDaf.n)} ע״{gemaraDaf.side === "a" ? "א" : "ב"}</b>
                {nextDafRef && <button className="mini-btn" onClick={() => window.loadSicha && window.loadSicha(nextDafRef)}>Next daf</button>}
              </span>
            )}
            {letterNumber && <span className="reader-quick-chip">Letter {letterNumber}</span>}
          </div>
        </header>

        <div className="read-metarow">
          <button className="chip chip-btn chip-sources-cta" onClick={() => { setShowGraph(false); setTab("sources"); setRailCollapsed(false); if (isMobile) openMobileSheet(); if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("sources_panel_opened", { surface: "reader", trigger: "metadata" }); }} aria-label={`Open ${sicha.meta.quotedSources ?? quotedSources.length} quoted sources`}>
            <Icon d="graph" size={13} /> {sicha.meta.quotedSources ?? quotedSources.length} quoted sources <Icon d="chev" size={12} />
          </button>
          {sicha.translationCoverage && <span className="chip"><Icon d="trans" size={13} /> {sicha.translationCoverage.translatedSegments.toLocaleString()} / {sicha.translationCoverage.totalSegments.toLocaleString()} translated ({sicha.translationCoverage.percent}%)</span>}
          <span className="chip"><Icon d="text" size={13} /> ~{sicha.meta.readMins} min</span>
          <span className="chip"><span className="dot" style={{ background: "var(--t-rebbe)" }} /> {sicha.meta.translated ? "Bilingual" : "Source language"}</span>
          <button className="chip chip-btn" onClick={() => { setShowGraph(true); setTab("sources"); setRailCollapsed(false); if (isMobile) openMobileSheet(); if (window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("sources_panel_opened", { surface: "reader", trigger: "path" }); }}>
            <Icon d="path" size={13} /> Source path
          </button>
        </div>

        <article data-reading-font={readerFontValue(readFont)} className={`read-body layout-${bothLangs ? effLayout : "stacked"} lang-${effectiveLang} footnotes-${isMobile ? effFnMode : fnStyle}`} style={{ fontSize: `calc(${readScale}px * var(--read-scale))` }}>
          {sicha.segments.map((seg, segIndex) => {
              const segmentIdentity = seg.ref || `${seg._sectionRef || sicha.id || "section"}:${seg.id || segIndex}`;
              const isActiveSeg = activeSegId === segmentIdentity;
              const segmentFootnotes = segSources(seg);
              const inlineSources = inlineFootnotes
                ? segmentFootnotes.filter((source) => inlineOpenAll || fnInline.includes(source.n))
                : [];
              const heBlock = showHe && (
                <p key="he" lang="he" dir="rtl" className="seg-he heb">
                  {seg.openingLetter && <strong className="reader-section-letter">{seg.openingLetter}. </strong>}
                  {renderWithMarkers(seg.he, { onOpen: handleMarker, activeN: hoverN || focusedMarkerN || openN, onHover: setHoverN, highlight: activeSearchQuery, inlineSources, inlineLanguage: "he", renderInlineText: true, footnoteNumbers: sources.filter((source) => source.footnote).map((source) => source.n) })}
                </p>
              );
              const enBlock = showEn && (
                <p key="en" lang="en" dir="ltr" className="seg-en serif">
                  {renderWithMarkers(seg.en, { onOpen: handleMarker, activeN: hoverN || focusedMarkerN || openN, onHover: setHoverN, highlight: activeSearchQuery, inlineSources, inlineLanguage: "en", renderInlineText: true, footnoteNumbers: sources.filter((source) => source.footnote).map((source) => source.n) })}
                </p>
              );
              // Stacked / single-language: one flowing column. Side-by-side only when both langs show.
              const isGrid = (effLayout === "split" || effLayout === "columns") && bothLangs;
              // split → English left, Hebrew right · columns → Hebrew left, English right (the mirror)
              const blocks = (effLayout === "split" ? [enBlock, heBlock] : [heBlock, enBlock]).filter(Boolean);
              return (
                <div key={segmentIdentity}
                  className={"seg" + (isGrid ? " seg-both seg-" + effLayout : " seg-stacked") + (isActiveSeg ? " is-current" : "")}
                  data-seg-id={seg.id}
                  data-seg-ref={seg.ref || seg._sectionRef || undefined}
                  aria-current={isActiveSeg ? "location" : undefined}
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    if (isMobile && event.target && event.target.closest && event.target.closest(".seg-text") && readerWordAtPoint(event)) {
                      event.currentTarget && event.currentTarget.blur && event.currentTarget.blur();
                      return;
                    }
                    selectSegment(seg.ref || seg.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      selectSegment(seg.ref || seg.id);
                    }
                  }}>
                  {seg.farbrengen && <header className="farbrengen-opening">
                    {segIndex > 0 && <h2 lang="he" dir="rtl" className="heb">{seg.farbrengen.title}</h2>}
                    <div><span>Vol. {seg.farbrengen.volume}</span>
                      {seg.farbrengen.pdf && <a href={seg.farbrengen.pdf} target="_blank" rel="noopener noreferrer" onKeyDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>Download PDF · Lahak ↗</a>}
                    </div>
                  </header>}
                  {/* section divider when a new letter/chapter is lazy-loaded */}
                  {seg._section && (
                    <div className="seg-divider" onClick={(event) => { event.stopPropagation(); seg._sectionRef && window.loadSicha && window.loadSicha(seg._sectionRef); }}>
                      <span lang="he" dir="rtl" className="heb">{seg._section}</span>
                    </div>
                  )}
                  <div className={"seg-text" + (isGrid ? " seg-text-both seg-text-" + effLayout : " seg-text-stacked")}>
                    <span className="seg-number" aria-hidden="true">{readerSegmentNumber(seg, segIndex)}</span>
                    {blocks}
                  </div>
                </div>
              );
            })}
          {sicha._gathering && (
            <>
              {/* Ghost paragraphs keep the page's shape while the rest of the
                  letter arrives; screen readers get one polite status line. */}
              <div className="read-skeleton" aria-hidden="true">
                {[0, 1, 2].map((index) => (
                  <span key={index} style={{ width: `${94 - index * 13}%` }} />
                ))}
              </div>
              <p className="sr-only" role="status" aria-live="polite">Gathering the rest of this sefer…</p>
            </>
          )}
          <div className="read-end">∎</div>
        </article>
        {maamarContinuation && (
          <div className="read-continue">
            <p className="read-continue-kicker">End of this maamar</p>
            <button type="button" className="read-continue-btn" onClick={() => jumpSection(maamarContinuation.ref)}>
              <span className="read-continue-text">
                {maamarContinuation.heTitle && (
                  <b lang="he" dir="rtl" className="heb">{maamarContinuation.heTitle}</b>
                )}
                <i>{maamarContinuation.title}</i>
              </span>
              <Icon d="back" size={16} style={{ transform: "scaleX(-1)" }} />
            </button>
          </div>
        )}
        {nextLoadState === "loading" && <p className="rail-hint" role="status" aria-live="polite">Loading the next text…</p>}
        {nextLoadState === "error" && <p role="alert">The next text could not be loaded. <button className="mini-btn" onClick={async () => {
          const controller = continuationControllerRef.current;
          const key = `${sicha.id || ""}\u0000${sicha.next || ""}`;
          nextLoadStateRef.current = "loading";
          setNextLoadState("loading");
          const loaded = await controller.retry(key);
          if (controller.key !== key) return;
          nextLoadStateRef.current = loaded ? "" : "error";
          setNextLoadState(nextLoadStateRef.current);
        }}>Try again</button></p>}
        <SiteFooter />
      </div>

      {/* context rail — desktop sidebar / mobile resizable bottom-sheet (non-modal) */}
      <aside className={"rail" + (isMobile ? " rail-sheet" : "") + (isMobile && sheetOpen ? " open" : "") + (dragging ? " dragging" : "")} ref={railRef}
        style={isMobile ? { height: curSheetH } : undefined}
        aria-label="Reader sources and tools" aria-hidden={isMobile ? !sheetOpen : railCollapsed}
        inert={isMobile && !sheetOpen ? true : undefined}>
        {isMobile && (
          <div className="sheet-grip">
            <button className="sheet-drag-handle" type="button" onPointerDown={startSheetDrag} onDoubleClick={toggleSheetHeight}
              aria-label="Drag to resize sources panel">
              <span />
            </button>
            <button className="sheet-size-toggle" type="button" onClick={toggleSheetHeight}
              aria-label={curSheetH > mobileViewportHeight() * 0.68 ? "Make sources panel smaller" : "Expand sources panel"}>
              {curSheetH > mobileViewportHeight() * 0.68 ? "Smaller" : "Expand"}
            </button>
            <button className="sheet-close" onClick={closeMobileSheet} aria-label="Close sources"><Icon d="chevd" size={18} /></button>
          </div>
        )}
        <div className="rail-tabs">
          {[["sources", "Sources", "graph"]].concat(toc && toc.sections && toc.sections.length > 1 ? [["contents", "Contents", "contents"]] : []).concat([["notes", "Saved", "note"], ["dictionary", "Dictionary", "book"], ["related", "Related", "layers"]]).map(([k, label, ic]) => (
            <button type="button" key={k} className={"rail-tab" + (tab === k ? " on" : "")} onClick={() => setTab(k)} title={label} aria-label={label}>
              <Icon d={ic} size={16} /><span className="rail-tab-label">{label}</span>
              {k === "notes" && savedCount > 0 && <span className="rail-badge">{savedCount}</span>}
              {k === "dictionary" && dictionarySavedCount > 0 && <span className="rail-badge">{dictionarySavedCount}</span>}
            </button>
          ))}
          {!isMobile && (
            <button className="rail-close" onClick={() => setRailCollapsed(true)} title="Hide sidebar" aria-label="Hide sidebar">
              <Icon d="close" size={15} />
            </button>
          )}
        </div>

        <div className="rail-body">
          {tab === "contents" && toc && (
            <div className="rail-toc">
              <div className="rail-h">{toc.book} <span>{toc.count} sections</span></div>
              <label className="rail-search rail-toc-search">
                <Icon d="search" size={15} />
                <input value={tocQ} onChange={(e) => setTocQ(e.target.value)} placeholder="Parsha, sicha, section, or phrase" />
                {tocQ && (
                  <button className="rail-search-clear" onClick={() => setTocQ("")} aria-label="Clear contents search" type="button">
                    <Icon d="close" size={13} />
                  </button>
                )}
              </label>
              <div className="rail-toc-meta">
                <span>{tocSearchMode ? `${tocSearchResults.length} matches` : `${selectedTocPrimary ? selectedTocPrimary.sectionCount : 0} sections`}</span>
                {tocQ && <button onClick={() => setTocQ("")}>Clear</button>}
              </div>
              {!tocSearchMode && tocPrimaryGroups.length > 1 && (
                <label className="rail-toc-select-label">
                  <span>Part of book</span>
                  <select aria-label="Choose contents group" value={selectedTocPrimary ? selectedTocPrimary.key : ""} onChange={(event) => setTocBrowseKey(event.target.value)}>
                    {tocPrimaryGroups.map((primary) => <option key={primary.key} value={primary.key}>{primary.label} · {primary.sectionCount}</option>)}
                  </select>
                </label>
              )}
              <div className="rail-toc-results">
                {tocSearchMode ? renderTocSearchResults(false) : renderTocGroups(selectedTocPrimary, false)}
              </div>
            </div>
          )}
          {tab === "sources" && !showGraph && (
            <div className="srclist">
              <div className="rail-h">Resources for this block <span>{effectiveActiveNs.length}</span></div>
              <div className="reader-discovery-tools" aria-label="Explore discovery tools">
                <button type="button" onClick={() => onOpenExplore && onOpenExplore({ viewMode: "browse" })}><Icon d="layers" size={14} /><span><b>Browse</b><i>Whole {(/maamar/i.test(sicha._liveRef || sicha.id || "") ? "maamar" : /sicha/i.test(sicha._liveRef || sicha.id || "") ? "sicha" : "text")}</i></span></button>
                <button type="button" onClick={() => onOpenExplore && onOpenExplore({ viewMode: "river", surprise: true })}><Icon d="compass" size={14} /><span><b>Surprise me</b><i>Open a connection</i></span></button>
              </div>
              {selectedSource && (
                <div className="rail-selected-source">
                  <div className="rail-selected-top">
                    <span className="srccard-n" style={{ background: typeVar(selectedSource.type) }}>{selectedSource.n}</span>
                    <div>
                      <b>{selectedSource.title}</b>
                      <span>{selectedSource.citation || selectedSource.srcRef || sourceGroupLabel(selectedSource)}</span>
                    </div>
                    <SourceLangBadges source={selectedSource} compact />
                    <button className="rail-selected-close" onClick={() => setOpenN(null)} aria-label="Clear selected source"><Icon d="close" size={14} /></button>
                  </div>
                  <div className="rail-selected-actions">
                    <button className={`mini-btn reader-save-source${isCollected(selectedSource) ? " is-saved" : ""}`} disabled={isCollected(selectedSource)} onClick={() => addWs(selectedSource)}>
                      <Icon d={isCollected(selectedSource) ? "check" : "plus"} size={13} /> {isCollected(selectedSource) ? "Saved for sheet" : "Save for source sheet"}
                    </button>
                    <button className="mini-btn" onClick={() => setFolderSource(selectedSource)}><Icon d="bookmark" size={13} /> Save to folder</button>
                    {selectedSource.anchorRef && (
                      <button className="mini-btn" onClick={() => focusSourceAnchor(selectedSource.anchorRef, selectedSource)}>
                        <Icon d="back" size={13} /> In text
                      </button>
                    )}
                    <button className="mini-btn" onClick={() => setTab("related")}>
                      <Icon d="layers" size={13} /> Related
                    </button>
                    <button className="mini-btn" onClick={() => window.openComparePair && window.openComparePair(selectedSource)}>
                      <Icon d="compare" size={13} /> Compare
                    </button>
                  </div>
                </div>
              )}
              {folderSource && (
                <div className="reader-folder-picker" role="dialog" aria-label="Save source to a folder">
                  <div className="reader-folder-picker-head"><span><b>Save to a folder</b><i>{folderSource.title || folderSource.citation}</i></span><button onClick={() => setFolderSource(null)} aria-label="Close folder picker"><Icon d="close" size={13} /></button></div>
                  {(ws.folders || []).length > 0 && <div className="reader-folder-options">{ws.folders.map((folder) => <button key={folder.id} onClick={() => saveSourceToFolder(folderSource, folder.id)}><Icon d="bookmark" size={13} /> {folder.name}<span>{(folder.items || []).length}</span></button>)}</div>}
                  <form className="reader-folder-new" onSubmit={createFolderAndSave}>
                    <input value={folderName} onChange={(event) => setFolderName(event.target.value)} placeholder={(ws.folders || []).length ? "New folder name" : "Saved sources"} aria-label="New saved folder name" />
                    <button className="mini-btn" type="submit"><Icon d="plus" size={12} /> Create & save</button>
                  </form>
                </div>
              )}
              <div className="rail-tools">
                <button className={"rail-tool" + (railTool === "about" ? " on" : "")} onClick={() => setRailTool(railTool === "about" ? null : "about")}>
                  <Icon d="globe" size={17} />
                  <span>About this Text</span>
                </button>
                <button className={"rail-tool" + (railTool === "translations" ? " on" : "")} onClick={() => setRailTool(railTool === "translations" ? null : "translations")}>
                  <Icon d="trans" size={17} />
                  <span>Translations</span>
                  <em>{versionCounts.total || (sicha.meta.translated ? 1 : 0)}</em>
                </button>
              </div>
              {railTool === "about" && (
                <div className="rail-tool-panel">
                  <div className="rail-about-title">{sicha.titleEn}</div>
                  {sicha.titleHe && <div lang="he" dir="rtl" className="rail-about-he heb">{sicha.titleHe}</div>}
                  <div className="rail-about-stats">
                    <span><Icon d="text" size={13} /> {sicha.segments.length} loaded</span>
                    <span><Icon d="graph" size={13} /> {sources.length} resources</span>
                    <span><Icon d="trans" size={13} /> {versionCounts.total || (sicha.meta.translated ? 1 : 0)} versions</span>
                  </div>
                  <div className="rail-about-grid">
                    <span>Current ref</span><b>{canonicalCurrentTextRef || "Current text"}</b>
                    <span>Collection</span><b>{sicha.collection || "Library"}</b>
                    <span>Path</span><b>{(sicha.categories || []).join(" / ") || "Mayanos"}</b>
                    <span>Sections</span><b>{sicha.segmentCount || sicha.segments.length}</b>
                    <span>Direction</span><b>{sicha.primaryDirection === "rtl" ? "Hebrew first" : "English first"}</b>
                  </div>
                  <div className="rail-about-actions">
                    <button className="mini-btn" onClick={() => {
                      const ref = canonicalCurrentTextRef || sicha._liveRef || sicha.id || sicha.titleEn;
                      window.WS.addWorkingText({
                        id: `working-${String(ref).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`,
                        srcKey: `working:${ref}`,
                        n: `W${(window.WS.active().workingTexts || []).length + 1}`,
                        title: sicha.titleEn || ref,
                        heTitle: sicha.titleHe || "",
                        citation: ref,
                        srcRef: ref,
                        type: "Chassidus",
                        relationship: "Working text for research",
                      });
                    }} title="Keep this text available as a research anchor without adding it to a sheet">
                      <Icon d="layers" size={13} /> Use for research
                    </button>
                    {activeSectionInfo.prev && (
                      <button className="mini-btn" onClick={() => jumpSection(activeSectionInfo.prev)}>
                        <Icon d="back" size={13} /> Previous
                      </button>
                    )}
                    {activeSectionInfo.next && (
                      <button className="mini-btn" onClick={() => jumpSection(activeSectionInfo.next)}>
                        Next <Icon d="back" size={13} style={{ transform: "scaleX(-1)" }} />
                      </button>
                    )}
                    {sefariaTextUrl && (
                      <a className="mini-btn" href={sefariaTextUrl} target="_blank" rel="noreferrer">
                        <Icon d="external" size={13} /> Sefaria
                      </a>
                    )}
                    {correctionUrl && (
                      <a className="mini-btn" href={correctionUrl} target="_blank" rel="noreferrer">
                        <Icon d="note" size={13} /> Report text issue
                      </a>
                    )}
                  </div>
                </div>
              )}
              {railTool === "search" && (
                <div className="rail-tool-panel">
                  <label className="rail-search">
                    <Icon d="search" size={15} />
                    <input ref={searchInputRef} value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search this text"
                      onKeyDown={(e) => {
                        if (e.key !== "Enter" || !searchResults.length) return;
                        e.preventDefault();
                        jumpSearchResult(currentSearchIndex + (e.shiftKey ? -1 : 1));
                      }} />
                    {searchQ && (
                      <button className="rail-search-clear" onClick={clearSearch} aria-label="Clear search" type="button">
                        <Icon d="close" size={13} />
                      </button>
                    )}
                  </label>
                  {searchQ.trim().length >= 2 && (
                    <>
                      <div className="rail-search-meta">
                        <span className="rail-search-count">
                          {bookSearchLoading ? "Searching the whole book…" : bookSearchError ? "Search unavailable" : searchResults.length ? `${currentSearchIndex + 1} of ${searchResults.length} matches` : "No matches"}
                        </span>
                        <span className="rail-search-nav">
                          <button onClick={() => jumpSearchResult(currentSearchIndex - 1)} disabled={!searchResults.length} aria-label="Previous search result" title="Previous result">
                            <Icon d="back" size={12} />
                          </button>
                          <button onClick={() => jumpSearchResult(currentSearchIndex + 1)} disabled={!searchResults.length} aria-label="Next search result" title="Next result">
                            <Icon d="back" size={12} style={{ transform: "scaleX(-1)" }} />
                          </button>
                          <button onClick={clearSearch}>Clear</button>
                        </span>
                      </div>
                      <div className="rail-search-results">
                        {searchResults.length ? searchResults.map((r, i) => (
                          <button key={r.ref || r.index} className={"rail-search-result" + (i === currentSearchIndex ? " on" : "")} onClick={() => jumpSearchResult(i)}>
                            <span>{r.ref || `${sicha.titleEn} ${r.index + 1}`}</span>
                            <em>{renderHighlightedPlain((r.en || r.he).slice(0, 130), activeSearchQuery, `res-${r.ref || r.index}`)}</em>
                          </button>
                        )) : bookSearchError ? <div className="rail-search-error"><p className="rail-hint">{bookSearchError}</p><button className="mini-btn" onClick={() => setBookSearchRetry((value) => value + 1)}>Try again</button></div> : <p className="rail-hint">No matches in this book.</p>}
                      </div>
                    </>
                  )}
                </div>
              )}
              {railTool === "translations" && (
                <div className="rail-tool-panel">
                  <div className="rail-version-head">
                    <b>Reading mode</b>
                    <span>{versionCounts.total || (sicha.meta.translated ? 1 : 0)} version{(versionCounts.total || (sicha.meta.translated ? 1 : 0)) === 1 ? "" : "s"}</span>
                  </div>
                  <div className="rail-version-row">
                    <button className={"mini-btn" + (effectiveLang === "en" ? " on" : "")} disabled={!hasEnglishText} onClick={() => onLang && onLang("en")}>EN</button>
                    <button className={"mini-btn" + (effectiveLang === "both" ? " on" : "")} disabled={!hasHebrewText || !hasEnglishText} onClick={() => onLang && onLang("both")}>Both</button>
                    <button className={"mini-btn" + (effectiveLang === "he" ? " on" : "")} disabled={!hasHebrewText} onClick={() => onLang && onLang("he")}>עב</button>
                  </div>
                  {Array.isArray(sicha.alternateTexts) && sicha.alternateTexts.length > 0 && (
                    <label className="rail-alternate-texts">
                      <span>Other text sources</span>
                      <select value="" onChange={(event) => {
                        const ref = event.target.value;
                        if (ref && window.loadSicha) window.loadSicha(ref, { alternateTexts: sicha.alternateTexts || [] });
                      }}>
                        <option value="">Choose a source…</option>
                        {sicha.alternateTexts.map((source) => (
                          <option key={source.ref} value={source.ref}>{source.label || source.ref}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  {["he", "en"].map((k) => {
                    const versions = ((sicha.versionsAvailable || {})[k] || []);
                    const details = (sicha.versionDetails || []).filter((version) => version.language === k);
                    const active = k === "he" ? effectiveLang !== "en" : effectiveLang !== "he";
                    const selectedVersion = (sicha.selectedVersions || {})[k] || "";
                    return (
                      <div className={"rail-version-list" + (active ? " on" : "")} key={k}>
                        <span><Icon d={k === "he" ? "book" : "trans"} size={13} /> {k === "he" ? "Hebrew" : "English"} <em>{versions.length}</em></span>
                        {details.length ? details.map((version) => (
                          <button type="button" className={"rail-version-detail" + (selectedVersion === version.title ? " on" : "")} key={`${k}-${version.title}`}
                            onClick={() => window.loadSichaVersion && window.loadSichaVersion(k, version.title)}>
                            <b>{version.title}{selectedVersion === version.title ? " · Selected" : ""}</b>
                            <i>{[version.license, version.status].filter(Boolean).join(" · ") || "License not reported"}</i>
                            {/^https?:\/\//.test(version.source || "")
                              ? <a href={version.source} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>Version source <Icon d="external" size={11} /></a>
                              : version.source ? <i>{version.source}</i> : null}
                          </button>
                        )) : versions.length ? versions.map((v) => <b key={v}>{v}</b>) : <i>No {k === "he" ? "Hebrew" : "English"} versions reported.</i>}
                      </div>
                    );
                  })}
                </div>
              )}
              {activeSourceGroups.length === 0 && betaFootnotes.length === 0 && (
                <p className="rail-hint">No sources are tied to this block yet.</p>
              )}
              {activeSourceGroups.filter(({ group }) => group === "Quoted Footnotes").map(renderSourceGroup)}
              {betaFootnotes.length > 0 && (
                <div className="rail-resource-group beta-footnotes-group">
                  <button className={"rail-resource-head beta-footnotes-head" + (betaOpen ? " on" : "")} onClick={() => setBetaOpen((value) => !value)}>
                    <span className="rail-resource-book"><Icon d="search" size={18} /></span>
                    <span>Beta Footnotes</span>
                    <em>{betaFootnotes.length}</em>
                    <Icon d={betaOpen ? "chevd" : "chev"} size={14} />
                  </button>
                  {betaOpen && (
                    <div className="beta-footnotes-list">
                      <p className="beta-footnotes-notice">Live suggestions only. Existing footnotes and confirmed links are unchanged.</p>
                      {betaFootnotes.map((item) => (
                        <div className="beta-footnote-item" key={item.id}>
                          <div className="beta-footnote-title">
                            <span className="beta-badge">BETA</span>
                            <b>Footnote {item.marker}</b>
                            <span>{item.title}</span>
                          </div>
                          {item.text && (
                            <details className="beta-footnote-source-text">
                              <summary>View triggering footnote text</summary>
                              <p>{item.text}</p>
                            </details>
                          )}
                          {item.state === "loading" && <p className="beta-footnote-muted">Searching the Chassidus library…</p>}
                          {item.state === "unavailable" && <p className="beta-footnote-muted">Beta lookup is temporarily unavailable.</p>}
                          {item.state === "ready" && !item.matches.length && <p className="beta-footnote-muted">No beta candidates found for this footnote.</p>}
                          {item.state === "ready" && item.matches.map((match) => (
                            <div className="beta-match" key={match.id}>
                              <div className="beta-match-head">
                                <span className={"beta-confidence " + match.confidence}>{match.confidence}</span>
                                <b>{match.title}</b>
                                {match.heTitle && <span className="beta-he-title">{match.heTitle}</span>}
                                <span className="beta-target-type">{match.targetType === "passage" ? "Passage candidate" : "Work candidate"}</span>
                                {match.evidence.locatorNumber && (
                                  <span className="beta-locator-normalized">
                                    {match.evidence.locatorKind === "volume" ? "Volume" : "Chapter"} {match.evidence.locatorNumber}
                                  </span>
                                )}
                                {match.evidence.candidateTargetCount > 1 && <span className="beta-ambiguous">Needs volume</span>}
                                <span className="beta-origin">{match.origin === "sefaria_index" ? "Sefaria index" : match.origin === "unified_registry" ? "Unified registry" : "Local corpus"}</span>
                              </div>
                              {match.excerpt && <p className="beta-match-excerpt">{match.excerpt}</p>}
                              <p className="beta-match-reason">{match.reason}</p>
                              <p className="beta-match-evidence">Matched “{match.evidence.matchedAlias}”{match.evidence.locator ? ` · ${match.evidence.locator}` : ""}</p>
                              {match.evidence.candidateTargetCount > 1 && (
                                <p className="beta-alternatives">Possible volumes: {(match.evidence.alternativeTargets || []).join(" · ")}</p>
                              )}
                              {match.evidence.query && (
                                <details className="beta-search-evidence">
                                  <summary>Search evidence</summary>
                                  <p>{match.evidence.query}</p>
                                </details>
                              )}
                              {match.ref && <button type="button" className="mini-btn beta-open-match" onClick={() => window.openSource && window.openSource({
                                id: match.id, title: match.title, he: "", en: "", citation: match.ref, srcRef: match.ref,
                                relationship: "Beta suggestion", type: "Chassidus", beta: true,
                              })}><Icon d="external" size={12} /> Open candidate</button>}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {activeSourceGroups.filter(({ group }) => group !== "Quoted Footnotes").map(renderSourceGroup)}
            </div>
          )}
          {tab === "sources" && showGraph && (
            <div className="rail-graph">
              <div className="rail-h">Source map <span className="rail-h-actions"><button className="mini-btn" onClick={() => onOpenExplore && onOpenExplore()}><Icon d="external" size={12} /> Full map</button><button className="mini-btn" onClick={() => setShowGraph(false)}>List view</button></span></div>
              <SourceGraph sources={sources} height={300} active={hoverN ? (sources.find((s) => s.n === hoverN) || {}).id : null}
                onHover={(id) => { const s = id ? sources.find((x) => x.id === id) : null; setHoverN(s ? s.n : null); }} compact />
              <div className="graph-legend">
                {[...new Set(quotedSources.map((s) => s.type))].map((t) => (
                  <span key={t} className="leg"><TypeDot type={t} size={7} /> {t}</span>
                ))}
              </div>
              <p className="rail-hint">Hover a node to trace it in the text. Click to preview the full source.</p>
            </div>
          )}
          {tab === "dictionary" && (
            <DictionaryLookup key={dictionaryQuery || "dictionary"} initialQuery={dictionaryQuery} />
          )}
          {tab === "notes" && (
            ws.collected.length === 0 && bookmarks.length === 0 && savedFolderCount === 0 && (ws.folders || []).length === 0 ? (
              <div className="rail-empty"><Icon d="note" size={26} style={{ color: "var(--text-3)" }} />
                <p><b>Nothing saved yet</b></p><span className="rail-hint">Save a source for a source sheet, bookmark your place, or keep sources in a personal folder.</span>
                <button className="mini-btn" onClick={() => window.WS.createFolder("Saved sources")}><Icon d="plus" size={12} /> Create saved folder</button></div>
            ) : (
              <div className="srclist">
                <div className="rail-h reader-saved-head">Saved <span className="rail-h-actions">{savedCount > 0 && <button className="mini-btn" onClick={() => { clearBookmarks(); window.WS.clearSaved(); }}><Icon d="close" size={12} /> Clear all</button>}</span></div>
                {bookmarks.length > 0 && (
                  <div className="bookmark-list">
                    <div className="rail-h">Bookmarks <span className="rail-h-actions"><em>{bookmarks.length}</em><button className="mini-btn" onClick={clearBookmarks}>Clear</button></span></div>
                    {bookmarks.map((ref) => (
                      <div key={ref} className={"bookmark-item" + (ref === bookmarkRef ? " on" : "")}>
                        <button onClick={() => openBookmark(ref)}>
                          <Icon d="bookmark" size={14} fill={ref === bookmarkRef ? "currentColor" : undefined} />
                          <span><b>{ref}</b><i>{ref === currentTextRef ? "Current section" : ref.indexOf(currentTextRef + ":") === 0 ? "Current text" : "Saved place"}</i></span>
                        </button>
                        <button className="bookmark-remove" onClick={() => removeBookmark(ref)} aria-label={`Remove bookmark ${ref}`}><Icon d="close" size={13} /></button>
                      </div>
                    ))}
                  </div>
                )}
                {ws.collected.length > 0 && (
                  <>
                    <div className="rail-h">Saved for source sheets <span className="rail-h-actions"><em>{ws.collected.length}</em><button className="mini-btn" onClick={() => window.WS.clearCollected()}>Clear</button></span></div>
                    {ws.collected.map((token) => window.WS.sourceByToken(token)).filter(Boolean).map((s) => (
                      <div key={window.WS.sourceKey(s)} className="ws-item"><span className="srccard-n" style={{ background: typeVar(s.type) }}>{s.n}</span>
                        <div><b>{s.title}</b> {s.citation}<div className="srccard-rel"><TypeDot type={s.type} size={6} /> {s.relationship}</div></div>
                        <button className="saved-source-remove" onClick={() => window.WS.uncollect(s)} aria-label={`Remove ${s.title} from source-sheet saves`}><Icon d="close" size={12} /></button>
                      </div>
                    ))}
                    <p className="reader-saved-explainer">Saving keeps the source handy; it does not add it to a sheet. In Research, choose the passage and place it wherever you need it.</p>
                    <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: 6 }} onClick={() => onOpenResearch && onOpenResearch()}><Icon d="layers" size={15} /> Build the source sheet</button>
                  </>
                )}
                {(ws.folders || []).map((folder) => (
                  <div className="reader-saved-folder" key={folder.id}>
                    <div className="rail-h"><span className="reader-folder-title"><Icon d="bookmark" size={13} /> {folder.name}</span><span className="rail-h-actions"><em>{(folder.items || []).length}</em>{folder.items && folder.items.length > 0 && <button className="mini-btn" onClick={() => window.WS.clearFolder(folder.id)}>Clear</button>}<button className="mini-btn" onClick={() => window.WS.deleteFolder(folder.id)} aria-label={`Delete folder ${folder.name}`}><Icon d="close" size={11} /></button></span></div>
                    {(folder.items || []).map((token) => window.WS.sourceByToken(token)).filter(Boolean).map((source) => (
                      <div className="ws-item" key={`${folder.id}-${window.WS.sourceKey(source)}`}><span className="srccard-n" style={{ background: typeVar(source.type) }}>{source.n}</span><div><b>{source.title}</b> {source.citation}<div className="srccard-rel"><TypeDot type={source.type} size={6} /> {source.relationship}</div></div><button className="saved-source-remove" onClick={() => window.WS.removeFromFolder(source, folder.id)} aria-label={`Remove ${source.title} from ${folder.name}`}><Icon d="close" size={12} /></button></div>
                    ))}
                    {(!folder.items || folder.items.length === 0) && <p className="rail-hint">This folder is empty. Use “Save to folder” on any source.</p>}
                  </div>
                ))}
                <form className="reader-folder-new reader-folder-create" onSubmit={(event) => { event.preventDefault(); window.WS.createFolder(folderName || "Saved sources"); setFolderName(""); }}>
                  <input value={folderName} onChange={(event) => setFolderName(event.target.value)} placeholder="New folder name" aria-label="New saved folder name" />
                  <button className="mini-btn" type="submit"><Icon d="plus" size={12} /> Add folder</button>
                </form>
              </div>
            )
          )}
          {tab === "related" && (
            <div className="srclist">
              <div className="rail-h">Related to this block <span>{activeNs.length}</span></div>
              {activeNs.length ? sources.filter((s) => activeSet.has(s.n)).slice(0, 10).map((s) => (
                <button key={s.id} className="related-item related-live" onClick={() => revealSourceInRail(s)}>
                  <span className="related-live-top">
                    <TypeDot type={s.type} size={8} />
                    <b>{s.title}</b>
                    <SourceLangBadges source={s} compact />
                  </span>
                  <span>{s.citation || s.srcRef}<i>{sourceGroupLabel(s)} · {s.relationship}</i></span>
                </button>
              )) : <p className="rail-hint">Scroll through the text and related resources will appear here.</p>}
              {resourceBuckets.length > 0 && (
                <>
                  <div className="rail-h" style={{ marginTop: 16 }}>Resources for this block</div>
                  {resourceBuckets.map((bucket) => {
                    const activeResources = bucket.resources.filter((resource) => relatedResourceMatchesSegment(resource, activeSegmentRef));
                    if (!activeResources.length) return null;
                    const expanded = !!expandedResourceGroups[bucket.kind];
                    const shownResources = expanded ? activeResources : activeResources.slice(0, 10);
                    return (
                      <div className="related-group" key={bucket.kind}>
                        <div className="related-group-head"><Icon d={resourceKindIcon(bucket.kind)} size={15} /> {bucket.label} <span>{activeResources.length}</span></div>
                        {shownResources.map((resource, i) => (
                          <React.Fragment key={`${resource.kind}:${resource.id}`}>
                            {i === 0 && <div className="resource-subhead">For this block</div>}
                            <div className={"related-resource-card" + (relatedResourceMatchesSegment(resource, activeSegmentRef) ? " on" : "")}>
                              <span className="related-resource-icon"><Icon d={resourceKindIcon(resource.kind)} size={16} /></span>
                              <span className="related-resource-main">
                                <span className="related-resource-title"><b>{resource.title}</b><SourceLangBadges source={resource} compact /></span>
                                {resource.heTitle && <em lang="he" dir="rtl" className="heb">{resource.heTitle}</em>}
                                {resource.description && <i>{resource.description}</i>}
                                <small>{[resource.source, typeof resource.count === "number" ? `${resource.count} sources` : "", resource.anchorRef ? "Linked in text" : ""].filter(Boolean).join(" · ")}</small>
                              </span>
                              <span className="related-resource-actions">
                                {resource.anchorRef && <button className="related-anchor-btn" onClick={() => scrollToSegment(resource.anchorRef)}>
                                  <Icon d="back" size={13} /> In text
                                </button>}
                                {resource.url && <a className="related-anchor-btn" href={resource.url} target="_blank" rel="noreferrer">
                                  <Icon d="external" size={13} /> Open
                                </a>}
                              </span>
                            </div>
                          </React.Fragment>
                        ))}
                        {activeResources.length > 10 && (
                          <button className="related-more" onClick={() => toggleMapKey(setExpandedResourceGroups, bucket.kind)}>
                            {expanded ? "See less" : `See all ${activeResources.length}`}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </aside>

      {!isMobile && railCollapsed && (
            <button className="rail-reopen" type="button" onClick={openAllFootnotes} title="Show sidebar and open all footnotes" aria-label="Show sidebar and open all footnotes">
          <Icon d="layers" size={15} /> <span>Sidebar</span>
        </button>
      )}

      {/* mobile: floating button to open the source sheet */}
      {isMobile && !sheetOpen && (
        <button className="sheet-fab" type="button" onClick={openSheet}
          aria-label={`Open sources panel, ${activeNs.length || sources.length} sources for the current passage`}
          title="Sources for this passage">
          <Icon d="link" size={18} />
          <span className="sheet-fab-n">{activeNs.length || sources.length}</span>
        </button>
      )}

      {collectionNotice && (
        <div className="reader-collect-toast" role="status">
          <span className="reader-collect-check"><Icon d="check" size={15} /></span>
          <span><b>{collectionNotice.folder ? "Saved to your folder" : "Saved for your source sheet"}</b><i>{collectionNotice.title} is waiting in Saved.</i></span>
          <button onClick={() => { setTab("notes"); setCollectionNotice(null); setRailCollapsed(false); if (isMobile) openMobileSheet(); }}>{collectionNotice.folder ? "Open Saved" : "View saved"}</button>
          <button className="reader-collect-close" onClick={() => setCollectionNotice(null)} aria-label="Dismiss saved-source message"><Icon d="close" size={13} /></button>
        </div>
      )}

      {dictionaryPeek && (
        <DictionaryPopover query={dictionaryPeek.query} pos={dictionaryPeek.pos}
          onClose={() => setDictionaryPeek(null)}
          onOpenPanel={() => openDictionaryPanel(dictionaryPeek.query)} />
      )}

      {/* mobile footnote peek popover */}
      {isMobile && (
        <FootnotePopover src={popSrc} pos={fnPop ? fnPop.pos : null} flip={fnPop ? fnPop.flip : false}
          onClose={() => setFnPop(null)}
          onFull={(n) => { setFnPop(null); openSource(n); }} />
      )}

      {/* mobile gesture affordances */}
      {isMobile && langToast && <div className="lang-toast">{langToast}</div>}
      {isMobile && swipeHint && !sheetOpen && (
        <div className="gesture-hint">
          <span><Icon d="path" size={13} /> Swipe ⇄ to switch language</span>
        </div>
      )}

      {/* table of contents (live): jump to any section of this book */}
      {tocOpen && toc && (
        <div className="toc-scrim" onClick={() => setTocOpen(false)}>
          <div className="toc-panel contents-picker" onClick={(e) => e.stopPropagation()}>
            <div className="toc-head">
              <div><b>{toc.book}</b><span className="toc-count">{toc.count} sections</span></div>
              <button className="icon-btn" onClick={() => setTocOpen(false)} aria-label="Close"><Icon d="close" size={18} /></button>
            </div>
            <div className="toc-search-wrap">
              <label className="rail-search toc-search">
                <Icon d="search" size={15} />
                <input ref={tocOverlayInputRef} value={tocQ} onChange={(e) => setTocQ(e.target.value)} placeholder="Search title, maamar, sicha, sefer, section, Hebrew, or opening words" aria-label="Search this book's contents" />
                {tocQ && (
                  <button className="rail-search-clear" onClick={() => setTocQ("")} aria-label="Clear contents search" type="button">
                    <Icon d="close" size={13} />
                  </button>
                )}
              </label>
              <div className="toc-meta">
                <span>{tocSearchMode ? `${tocSearchResults.length} matching ${tocSearchResults.length === 1 ? "section" : "sections"}` : "Browse the book, then choose an exact section"}</span>
                <span className="toc-key-hint">↑↓ select · Enter open · Esc close</span>
              </div>
            </div>
            <div className={"toc-browser" + (tocSearchMode ? " is-searching" : "")}>
              {!tocSearchMode && (
                <nav className="toc-outline" aria-label="Book outline">
                  <div className="toc-outline-title">Book outline</div>
                  {tocPrimaryGroups.map((primary) => {
                    const selected = selectedTocPrimary && primary.key === selectedTocPrimary.key;
                    const current = currentTocPrimary && primary.key === currentTocPrimary.key;
                    return (
                      <button key={primary.key} className={(selected ? "on" : "") + (current ? " current" : "")} onClick={() => setTocBrowseKey(primary.key)}>
                        <span>{primary.label}</span>
                        <small>{primary.groups.length} {primary.groups.length === 1 ? "part" : "parts"} · {primary.sectionCount}</small>
                        {current && <em>Here</em>}
                      </button>
                    );
                  })}
                </nav>
              )}
              <div className="toc-main">
                {tocSearchMode ? (
                  <>
                    <div className="toc-main-head">
                      <div><b>Search results</b><span>Across all {toc.count} sections</span></div>
                      <button onClick={() => setTocQ("")}>Browse outline</button>
                    </div>
                    {renderTocSearchResults(true)}
                  </>
                ) : (
                  <>
                    <div className="toc-main-head">
                      <div>
                        <b>{selectedTocPrimary && selectedTocPrimary.label}</b>
                        <span>{selectedTocPrimary ? `${selectedTocPrimary.groups.length} ${selectedTocPrimary.groups.length === 1 ? "part" : "parts"} · ${selectedTocPrimary.sectionCount} sections` : ""}</span>
                      </div>
                      {currentTocPrimary && selectedTocPrimary && currentTocPrimary.key !== selectedTocPrimary.key && (
                        <button onClick={() => setTocBrowseKey(currentTocPrimary.key)}>Return to current</button>
                      )}
                    </div>
                    {renderTocGroups(selectedTocPrimary, true)}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* print: setup modal + paper-optimized document (portaled to <body>) */}
      <PrintModal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        onConfirm={setPrintOpts}
        sicha={sicha}
        sources={sources}
        currentSectionRef={printTargetRef || currentTextRef}
        tocSections={displayTocSections}
      />
      <PrintDocument opts={printOpts} sicha={sicha} sources={sources} tocSections={displayTocSections} />
    </div>
  );
}

Object.assign(window, { Reader });
